import React, { useState, useMemo, useEffect } from 'react';
import { SkeletonLines } from '@/components/Admin/UI/Skeleton';
import { useSearchParams } from 'react-router-dom';
import styled, { keyframes, css } from 'styled-components';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { colors } from '@/constants/colors';
import PageTitle from '@/components/Admin/PageTitle';
import FileUploader from '@/components/Admin/UI/FileUploader';
import ResellerMarket from '@/components/Admin/ResellerMarket';
import { useUIStore } from '@/store/useUIStore';
import { useDistributorStatus } from '@/hooks/useDistributorStatus';
import { computeApprovalScore } from '@/utils/distributorApprovalScore';
import { formatRelativeDate } from '@/utils/formatRelativeDate';
import CreditTransfersTable from '@/components/Admin/CreditTransfersTable';
import { paginate } from '@/utils/transfers';
import { TransferCreditModal } from '@/pages/Admin/Vendo';
import { useMessageMember } from '@/features/community/CommunityDock';
import {
   getCreditReleaseStats,
   listPartners,
   listInquiries as apiListInquiries,
   getCreditPackageTiers,
   submitInquiryProof as apiSubmitInquiryProof,
   getMyPointsApi,
   applyResellerApi,
   getMyResellerApi,
   cancelMyResellerApi,
   listMyResellersApi,
   decideResellerApi,
   listIncentivesApi,
   getMyReferralApi,
   convertPointsApi,
   redeemIncentiveApi,
   listMyRedemptionsApi,
   getCredits,
   type ApiPartner,
   type ApiInquiry,
   type InquiryStatusApi,
   type CreditPackageTier,
   type ApiPointsLedgerRow,
   type ApiReseller,
   type ApiIncentive,
   type ApiRedemption,
   type ApiPointsMechanics,
} from '@/api/vendo';
import { useFocusFromQuery } from '@/hooks/useFocusFromQuery';

// Server populates `distributor` on buyer-scope queries with displayName,
// businessName, city, region, defaultPricePerCredit.
type PopulatedDistributor = {
   _id: string;
   displayName?: string;
   businessName?: string;
   city?: string;
   region?: string;
   defaultPricePerCredit?: number;
};

/* ─── types ─────────────────────────────────────────────────── */

/**
 * Partner hierarchy (per friend's advice 2026-04-22):
 *   Direct → Distributor → (downstream sellers/resellers, NOT listed publicly)
 *
 * Only approved **Distributors** are surfaced on this page. They buy credits
 * from Direct (SmartFi) at distributor pricing and resell downstream through
 * their own private networks (sellers/resellers not shown here).
 */
type Rank = 'distributor';

// SmartFi-direct package list — sourced from the live menu via
// getCreditPackageTiers (admin-editable in the SmartFi admin panel).
// The legacy hardcoded DIRECT_PACKAGES const is gone — admin edits to
// credits/amount per bundle now propagate to this page automatically.

const DIRECT_PRICE_PER_CREDIT = 500; // baseline for "savings" math
const TOP_PARTNER_PRICE = 500; // typical distributor markup
const PARTNER_SAVINGS_PER_CREDIT = DIRECT_PRICE_PER_CREDIT - TOP_PARTNER_PRICE;

type Tab =
   | 'partners'
   | 'resellers'
   | 'direct'
   | 'my-inquiries'
   | 'my-points'
   | 'my-resellers';

const VALID_TABS: Tab[] = [
   'partners',
   'resellers',
   'direct',
   'my-inquiries',
   'my-points',
   'my-resellers',
];
const TAB_STORAGE_KEY = 'smartfi-partners-tab';
/** The monthly credit release panel on the Partners list — off for now. */
const SHOW_RELEASE_PANEL = false;

const loadTab = (): Tab => {
   try {
      const v = localStorage.getItem(TAB_STORAGE_KEY);
      if (v && (VALID_TABS as string[]).includes(v)) return v as Tab;
   } catch {}
   return 'partners';
};

const INQUIRY_STATUS_LABEL: Record<InquiryStatusApi, string> = {
   pending: 'Awaiting distributor',
   approved: 'Approved · pay distributor',
   fulfilled: 'Credits received',
   rejected: 'Rejected',
};

/* ─── page ──────────────────────────────────────────────────── */

// Initials fallback for partner avatars when photoUrl is missing.
const initialsOf = (name: string): string =>
   name
      .split(/\s+/)
      .filter(Boolean)
      .map((w) => w[0])
      .join('')
      .slice(0, 2)
      .toUpperCase();

const Partners = () => {
   const { handleShow } = useUIStore();
   const { isDistributor, pendingApplication, approvedApplication } = useDistributorStatus();
   const ownPartnerId = approvedApplication?.partnerCreated;
   // A notification about one of MY inquiries lands with ?inquiry=<id> — open
   // the My inquiries tab so the row can be found and flashed.
   const focusInquiry = useFocusFromQuery('inquiry');
   // ?tab=direct from the chat's "Top up credits" (Ray 2026-10-27) lands on Buy Credits.
   const [tab, setTab] = useState<Tab>(() => {
      if (focusInquiry) return 'my-inquiries';
      const q = new URLSearchParams(window.location.search).get('tab') as Tab | null;
      return q && (VALID_TABS as string[]).includes(q) ? q : loadTab();
   });

   useEffect(() => {
      try {
         localStorage.setItem(TAB_STORAGE_KEY, tab);
      } catch {}
   }, [tab]);

   // The tab is remembered across visits, so someone who used Resellers before
   // becoming a distributor would still land on it after the tab is hidden.
   // isDistributor arrives from a query, hence the effect rather than a guard
   // in loadTab().
   useEffect(() => {
      if (isDistributor && tab === 'resellers') setTab('partners');
   }, [isDistributor, tab]);

   // Real partner roster from the server. Already sorted by tier + volume.
   const { data: partners = [], isLoading: partnersLoading } = useQuery({
      queryKey: ['vendo', 'partners'],
      queryFn: listPartners,
   });

   const { data: releaseStats } = useQuery({
      queryKey: ['vendo', 'credit-release-stats'],
      queryFn: getCreditReleaseStats,
   });

   // Live credit-package menu — admin-editable on the SmartFi admin
   // panel. Drives the Buy Credits package grid; replaces the old
   // hardcoded DIRECT_PACKAGES const.
   const { data: directPackages = [] } = useQuery({
      queryKey: ['vendo', 'credit-package-tiers'],
      queryFn: getCreditPackageTiers,
   });

   // Reseller: the caller's own reseller record (gates the "Apply as Reseller"
   // button) + which partner's apply modal is open.
   const { data: myResellerData } = useQuery({
      queryKey: ['vendo', 'my-reseller'],
      queryFn: getMyResellerApi,
   });
   const myReseller = myResellerData?.reseller ?? null;
   const [resellerApplyFor, setResellerApplyFor] = useState<ApiPartner | null>(null);
   // Only non-distributors with no active reseller record can apply. A rejected
   // or self-cancelled (withdrawn) record is free to re-apply.
   const canApplyReseller =
      !isDistributor &&
      (!myReseller ||
         myReseller.status === 'rejected' ||
         myReseller.status === 'withdrawn');

   // Cancel a pending reseller request (inline two-step confirm) so the
   // applicant isn't stuck if their distributor never answers.
   const qc = useQueryClient();
   const [confirmCancelReseller, setConfirmCancelReseller] = useState(false);
   const cancelResellerMut = useMutation({
      mutationFn: cancelMyResellerApi,
      onSuccess: () => {
         setConfirmCancelReseller(false);
         qc.invalidateQueries({ queryKey: ['vendo', 'my-reseller'] });
         qc.invalidateQueries({ queryKey: ['vendo', 'reseller', 'me'] });
         toast.success('Request cancelled — you can now apply to another distributor.');
      },
      onError: (e: any) =>
         toast.error(e?.response?.data?.error || 'Could not cancel the request'),
   });

   // Buyer-scope inquiries the current user submitted. Server populates
   // `distributor` with displayName/businessName/city/region/defaultPricePerCredit.
   const { data: myInquiries = [], isLoading: inquiriesLoading } = useQuery({
      queryKey: ['vendo', 'inquiries', 'buyer'],
      queryFn: () => apiListInquiries({ scope: 'buyer' }),
   });

   const inquiryCounts = useMemo(() => {
      const pending = myInquiries.filter((i) => i.status === 'pending').length;
      const approved = myInquiries.filter((i) => i.status === 'approved').length;
      return { pending, approved, actionable: pending + approved };
   }, [myInquiries]);

   const totals = useMemo(
      () => ({
         partners: partners.length,
         sold: partners.reduce((s, p) => s + p.cumulativeCreditsSold, 0),
      }),
      [partners]
   );

   const [cityFilter, setCityFilter] = useState<string>('all');
   const [sortKey, setSortKey] = useState<'sold' | 'available'>('sold');
   // List by default (Ray 2026-10-27); a viewer's pick is remembered on this browser.
   const VIEW_KEY = 'smartfi.partners.view';
   const [view, setViewState] = useState<'list' | 'cards'>(() => {
      try {
         return window.localStorage.getItem(VIEW_KEY) === 'cards' ? 'cards' : 'list';
      } catch {
         return 'list';
      }
   });
   const setView = (v: 'list' | 'cards') => {
      setViewState(v);
      try {
         window.localStorage.setItem(VIEW_KEY, v);
      } catch {
         /* this visit only */
      }
   };
   // "Message" opens the chat on a 1-on-1 with the distributor.
   const chat = useMessageMember();

   const cityOptions = useMemo(() => {
      const cities = Array.from(
         new Set(partners.map((p) => p.city).filter(Boolean))
      ).sort((a, b) => a.localeCompare(b));
      return cities;
   }, [partners]);

   const visiblePartners = useMemo(() => {
      const filtered =
         cityFilter === 'all'
            ? partners
            : partners.filter((p) => p.city === cityFilter);
      const sorted = [...filtered].sort((a, b) => {
         // Online distributors first (Ray 2026-10-27): they can answer right now.
         const aOn = a.online ? 1 : 0;
         const bOn = b.online ? 1 : 0;
         if (aOn !== bOn) return bOn - aOn;
         // Distributors you can actually buy from (available > 0) always rank
         // above sold-out ones — so a newly-approved, well-stocked distributor
         // surfaces instead of sinking below empty ones.
         const aHas = a.availableCredits > 0 ? 1 : 0;
         const bHas = b.availableCredits > 0 ? 1 : 0;
         if (aHas !== bHas) return bHas - aHas;
         if (sortKey === 'available') {
            return b.availableCredits - a.availableCredits;
         }
         // Highest sold, then highest available as the tiebreaker.
         return (
            b.cumulativeCreditsSold - a.cumulativeCreditsSold ||
            b.availableCredits - a.availableCredits
         );
      });
      return sorted;
   }, [partners, cityFilter, sortKey]);

   const requestCode = (partner: ApiPartner) => {
      handleShow({
         template: 'inquire-credits',
         title: `Request credits from ${partner.displayName}`,
         windowType: 'desktop',
         data: { id: partner._id },
      });
   };

   const handlePackageCta = (pkg: CreditPackageTier) => {
      if (isDistributor) {
         handleBuyPackage(pkg);
         return;
      }
      // If a pending application exists, the per-package CTAs are hidden
      // (handled in render). This guard catches any edge-case re-entry.
      if (pendingApplication) {
         toast.info('Your application is still pending admin review.', {
            autoClose: 2500,
         });
         return;
      }
      openApplyModal();
   };

   const handleBuyPackage = (pkg: CreditPackageTier) => {
      handleShow({
         template: 'buy-credit-package',
         title: `Buy ${pkg.name} pack — ${pkg.credits} credits`,
         windowType: 'desktop',
         data: {
            // tier is now the bundle's free-form name (admin-editable)
            tier: pkg.name,
            credits: pkg.credits,
            amount: pkg.amount,
            pricePerCredit: pkg.pricePerCredit,
            label: pkg.name,
            bonusCredits: pkg.bonusCredits ?? 0,
            points: pkg.points ?? 0,
         },
      });
   };

   const pendingApprovalChance = useMemo(() => {
      if (!pendingApplication) return 0;
      return computeApprovalScore({
         facebookUrl: pendingApplication.facebookUrl,
         mayorsPermitUrl: pendingApplication.mayorsPermitUrl,
         bir2303Url: pendingApplication.bir2303Url,
         proofOfAddressUrl: pendingApplication.proofOfAddressUrl,
         qualifyingAnswers: pendingApplication.qualifyingAnswers,
      });
   }, [pendingApplication]);

   // An approved reseller doesn't "apply" — they UPGRADE, and the account
   // converts on approval (they stop being a reseller). Same form either way;
   // only the wording changes, and the modal spells out what converting means.
   const isUpgrade = myReseller?.status === 'approved';
   const applyLabel = isUpgrade ? 'Upgrade to Distributor' : 'Apply as Distributor';
   const applyModalTitle = isUpgrade
      ? 'Upgrade to a SmartFi Distributor'
      : 'Apply as a SmartFi Distributor';
   const openApplyModal = () =>
      handleShow({
         template: 'distributor-apply',
         title: applyModalTitle,
         windowType: 'desktop',
         data: null,
      });

   const canApplyDistributor = !isDistributor && !pendingApplication;

   // smartfi.ph/credits → "Apply as distributor" lands here with ?apply=distributor
   // (Ray 2026-09-16): open the application modal by itself, once, and drop
   // the flag from the address so a refresh does not reopen it.
   const [searchParams, setSearchParams] = useSearchParams();
   // ?tab=<name> while already on this page (the Request credits form lands on My Inquiries, Ray 2026-10-28):
   // switch, then drop it from the address so the remembered tab wins next time.
   useEffect(() => {
      const q = searchParams.get('tab');
      if (!q || !(VALID_TABS as string[]).includes(q)) return;
      setTab(q as Tab);
      const next = new URLSearchParams(searchParams);
      next.delete('tab');
      setSearchParams(next, { replace: true });
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [searchParams]);
   // The chat's "Buy credits" on a distributor's card lands here with ?buy=<partnerId> (Ray 2026-10-27).
   useEffect(() => {
      const id = searchParams.get('buy');
      if (!id || partnersLoading) return;
      const next = new URLSearchParams(searchParams);
      next.delete('buy');
      setSearchParams(next, { replace: true });
      const partner = partners.find((x) => x._id === id);
      if (partner && ownPartnerId !== id) requestCode(partner);
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [searchParams, partnersLoading, partners]);
   useEffect(() => {
      if (searchParams.get('apply') !== 'distributor') return;
      const next = new URLSearchParams(searchParams);
      next.delete('apply');
      next.delete('pack');
      setSearchParams(next, { replace: true });
      setTab('direct'); // land on Buy Credits, the packs behind the modal
      if (canApplyDistributor) openApplyModal();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [searchParams, canApplyDistributor]);
   const titleActions = canApplyDistributor
      ? [
           {
              title: applyLabel,
              type: 'button' as const,
              onClick: openApplyModal,
           },
        ]
      : [];

   return (
      <Wrapper>
         <PageTitle title="Buy License" actions={titleActions} />

         <TabBar>
            <TabBtn $active={tab === 'partners'} onClick={() => setTab('partners')}>
               Partners
               <TabSavings>save up to ₱{PARTNER_SAVINGS_PER_CREDIT}/credit</TabSavings>
            </TabBtn>
            {/* A distributor may not buy from a reseller — that would refill a
                distributor wallet from below, which is the return leg of the
                points loop. The server refuses it; hiding the tab means they
                never get as far as filling in a request that cannot succeed. */}
            {!isDistributor && (
               <TabBtn $active={tab === 'resellers'} onClick={() => setTab('resellers')}>
                  Resellers
               </TabBtn>
            )}
            <TabBtn $active={tab === 'direct'} onClick={() => setTab('direct')}>
               Buy Credits
            </TabBtn>
            <TabBtn
               $active={tab === 'my-inquiries'}
               onClick={() => setTab('my-inquiries')}
            >
               My Inquiries
               {inquiryCounts.actionable > 0 && (
                  <InquiryTabBadge>{inquiryCounts.actionable}</InquiryTabBadge>
               )}
            </TabBtn>
            <TabBtn $active={tab === 'my-points'} onClick={() => setTab('my-points')}>
               My Points ✨
            </TabBtn>
            {isDistributor && (
               <TabBtn
                  $active={tab === 'my-resellers'}
                  onClick={() => setTab('my-resellers')}
               >
                  My Resellers
               </TabBtn>
            )}
         </TabBar>

         {tab === 'my-points' && <MyPointsPane />}
         {tab === 'my-resellers' && <MyResellersPane />}
         {tab === 'resellers' && !isDistributor && <ResellerMarket />}

         {resellerApplyFor && (
            <ResellerApplyModal
               partner={resellerApplyFor}
               onClose={() => setResellerApplyFor(null)}
            />
         )}

         {tab === 'my-inquiries' && (
            <MyInquiriesPane
               inquiries={myInquiries}
               loading={inquiriesLoading}
               onBrowse={() => setTab('partners')}
            />
         )}

         {tab === 'direct' && (
            <DirectPane>
               {pendingApplication && (
                  <PendingAppCard>
                     <PendingAppIcon>⏳</PendingAppIcon>
                     <PendingAppBody>
                        <PendingAppTitle>Application awaiting review</PendingAppTitle>
                        <PendingAppMeta>
                           <strong>{pendingApplication.displayName}</strong> · submitted{' '}
                           {new Date(pendingApplication.createdAt).toLocaleDateString('en-PH', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                           })}
                        </PendingAppMeta>
                        <PendingAppText>
                           Admin typically reviews within 1–3 business days. Until then, you
                           can browse the Partners tab and inquire with an existing
                           distributor for credits.
                        </PendingAppText>
                     </PendingAppBody>
                     <PendingAppScore>
                        <PendingAppScoreNum $score={pendingApprovalChance}>
                           {pendingApprovalChance}%
                        </PendingAppScoreNum>
                        <PendingAppScoreLbl>chance of approval</PendingAppScoreLbl>
                     </PendingAppScore>
                  </PendingAppCard>
               )}
               <DirectIntro>
                  <DirectIntroTitle>⚡ Credit Store ⚡</DirectIntroTitle>
                  <DirectIntroDesc>
                     {isDistributor
                        ? 'Welcome, Distributor. Buy credit packages direct from SmartFi — larger packages unlock lower per-credit prices. 1 credit = 1 vendo device, lifetime license. Payment via Xendit.'
                        : pendingApplication
                        ? "We'll let you know once the admin reviews your application. The packages below preview what you'll be able to buy as an approved distributor."
                        : isUpgrade
                        ? 'Direct credit packages are for SmartFi Distributors. As a reseller you can upgrade below — you would buy your credits from SmartFi instead of from your distributor, and your credits and points come with you.'
                        : 'Direct credit packages are available to approved SmartFi Distributors only. Apply below to get access — regular owners can inquire with a distributor via the Partners tab.'}
                  </DirectIntroDesc>
               </DirectIntro>

               <PackageGrid>
                  {directPackages.length === 0 && (
                     <EmptyHint>
                        No credit packages configured yet — admin can add bundles
                        from Settings → Credit Package Bundles.
                     </EmptyHint>
                  )}
                  {directPackages.map((pkg, idx) => {
                     // Featured slot is the middle of the list (visual emphasis).
                     // For the default 6-tier seed this lands on Gold; for
                     // admin-customized lists it picks roughly the median.
                     const featured =
                        directPackages.length >= 4 &&
                        idx === Math.floor(directPackages.length / 2);
                     // Styled-component theming was tier-name keyed; for
                     // unknown bundle names we fall back to "Gold" so the
                     // gradients still render reasonably.
                     const themeKey = ['Starter', 'Bronze', 'Silver', 'Gold', 'Diamond', 'Legendary'].includes(pkg.name)
                        ? pkg.name
                        : 'Gold';
                     const pricePerCredit = Math.round(pkg.pricePerCredit);
                     const overCap =
                        isDistributor &&
                        !!releaseStats &&
                        releaseStats.monthlyLimit > 0 &&
                        pkg.credits > releaseStats.availableToRelease;
                     return (
                        <PackageCard key={pkg.name + idx} $featured={featured} $tier={themeKey}>
                           <PackageShine $tier={themeKey} />
                           {featured && <BestValueRibbon>Most Popular</BestValueRibbon>}
                           <PackageIconWrap $tier={themeKey}>
                              <PackageIcon>{pkg.icon || '🎟️'}</PackageIcon>
                              <PackageIconAura $tier={themeKey} />
                           </PackageIconWrap>
                           <PackageTag $tier={themeKey}>{pkg.name}</PackageTag>
                           <PackageHeader>
                              <PackageCreditsNum>{pkg.credits}</PackageCreditsNum>
                              <PackageCreditsLbl>credits</PackageCreditsLbl>
                           </PackageHeader>
                           {/* Wholesale pricing (bundle total + per-credit) is
                               distributor-only — regular owners shouldn't see the
                               distributor cost basis. Unlocks once approved. The
                               1-credit base bundle is the ₱500 end-user license,
                               so its price always shows. */}
                           {isDistributor || pkg.credits === 1 ? (
                              <>
                                 <PackagePrice>₱{pkg.amount.toLocaleString()}</PackagePrice>
                                 <PackagePerCredit>
                                    ₱{pricePerCredit}/credit
                                    {pkg.savings ? (
                                       <PackageSavings>
                                          save ₱{pkg.savings.toLocaleString()}
                                       </PackageSavings>
                                    ) : null}
                                 </PackagePerCredit>
                              </>
                           ) : (
                              <PackageLockedPrice>
                                 🔒 Distributor pricing
                              </PackageLockedPrice>
                           )}
                           {pkg.points || pkg.bonusCredits ? (
                              <PointsEarnWrap>
                                 {pkg.points ? (
                                    <PointsEarnBadge>
                                       ✨ earn{' '}
                                       {pkg.points.toLocaleString()} points!
                                    </PointsEarnBadge>
                                 ) : null}
                                 {pkg.bonusCredits ? (
                                    <PointsBonusFlag>
                                       🔥 +{pkg.bonusCredits.toLocaleString()}{' '}
                                       bonus credits
                                    </PointsBonusFlag>
                                 ) : null}
                              </PointsEarnWrap>
                           ) : null}
                           {/* Pending applicants don't see the apply CTA — the
                               pending-app card above already explains status. */}
                           {!pendingApplication &&
                              (() => {
                                 // 1-credit bundle is a teaser: non-distributors
                                 // get "Buy now" that routes them to the Partners
                                 // tab to convert into a buyer/partner.
                                 const oneCreditTeaser =
                                    pkg.credits === 1 && !isDistributor;
                                 return (
                                    <PackageBuyBtn
                                       $featured={featured}
                                       $tier={themeKey}
                                       disabled={overCap}
                                       onClick={() => {
                                          if (overCap) return;
                                          if (oneCreditTeaser) {
                                             setTab('partners');
                                             return;
                                          }
                                          handlePackageCta(pkg);
                                       }}
                                    >
                                       {overCap
                                          ? 'Exceeds monthly cap'
                                          : isDistributor
                                          ? 'Buy Now'
                                          : oneCreditTeaser
                                          ? 'Buy now'
                                          : applyLabel}
                                    </PackageBuyBtn>
                                 );
                              })()}
                        </PackageCard>
                     );
                  })}
               </PackageGrid>

               <SwitchHint>
                  💡 Prefer a local partner?{' '}
                  <SwitchLink onClick={() => setTab('partners')}>
                     Browse Partners (save up to ₱{PARTNER_SAVINGS_PER_CREDIT}/credit)
                  </SwitchLink>
               </SwitchHint>
            </DirectPane>
         )}

         {tab === 'partners' && (
            <>
               <PartnersTop>
               <Intro>
                  <IntroTitle>Authorized SmartFi Distributors</IntroTitle>
                  <IntroText>
                     Buy license credits from an approved distributor in your region. 1 credit
                     gives 1 vendo device a lifetime license (main or sub). Each distributor sets
                     their own pricing and can negotiate with you directly — contact them to
                     inquire.
                  </IntroText>
               </Intro>

               {/* Hidden for now (Ray 2026-10-27: "not making sense for now"). */}
               {SHOW_RELEASE_PANEL && releaseStats && (
                  <ReleasePanel>
                     <ReleaseHeader>
                        <ReleaseTitle>
                           SmartFi controls how many credits we release each month so
                           every distributor can actually sell what they buy.
                        </ReleaseTitle>
                        <ReleaseHint>
                           This protects partners from holding stale inventory.
                        </ReleaseHint>
                     </ReleaseHeader>

                     <ReleaseStats>
                        <ReleaseStat>
                           <ReleaseLbl>Available this month</ReleaseLbl>
                           <ReleaseVal
                              $tone={toneFromHeadroom(
                                 releaseStats.availableToRelease,
                                 releaseStats.monthlyLimit
                              )}
                           >
                              {releaseStats.availableToRelease.toLocaleString()}
                           </ReleaseVal>
                           <ReleaseSub>
                              of {releaseStats.monthlyLimit.toLocaleString()} cap
                           </ReleaseSub>
                        </ReleaseStat>
                        <ReleaseStat>
                           <ReleaseLbl>Partners' inventory</ReleaseLbl>
                           <ReleaseVal $tone="ok">
                              {releaseStats.partnerInventory.toLocaleString()}
                           </ReleaseVal>
                           <ReleaseSub>credits ready to sell to you</ReleaseSub>
                        </ReleaseStat>
                     </ReleaseStats>

                     <ReleaseBarTrack>
                        <ReleaseBarFill
                           $pct={
                              releaseStats.monthlyLimit > 0
                                 ? Math.min(
                                      100,
                                      Math.round(
                                         (releaseStats.releasedThisMonth /
                                            releaseStats.monthlyLimit) *
                                            100
                                      )
                                   )
                                 : 0
                           }
                           $tone={toneFromHeadroom(
                              releaseStats.availableToRelease,
                              releaseStats.monthlyLimit
                           )}
                        />
                     </ReleaseBarTrack>
                     <ReleaseBarLbl>
                        {releaseStats.releasedThisMonth.toLocaleString()} released ·{' '}
                        {totals.partners} active partner{totals.partners === 1 ? '' : 's'}
                     </ReleaseBarLbl>
                  </ReleasePanel>
               )}
               </PartnersTop>

               <FilterBar>
                  <FilterGroup>
                     <FilterLbl>City</FilterLbl>
                     <FilterSelect
                        value={cityFilter}
                        onChange={(e) => setCityFilter(e.target.value)}
                     >
                        <option value="all">All cities</option>
                        {cityOptions.map((c) => (
                           <option key={c} value={c}>
                              {c}
                           </option>
                        ))}
                     </FilterSelect>
                  </FilterGroup>
                  <FilterGroup>
                     <FilterLbl>Sort by</FilterLbl>
                     <FilterSelect
                        value={sortKey}
                        onChange={(e) => setSortKey(e.target.value as 'sold' | 'available')}
                     >
                        <option value="sold">Highest sold</option>
                        <option value="available">Highest available</option>
                     </FilterSelect>
                  </FilterGroup>
                  <FilterCount>
                     {visiblePartners.length} of {partners.length}
                  </FilterCount>
                  <ViewToggle role="group" aria-label="View">
                     <ViewBtn type="button" $active={view === 'list'} aria-pressed={view === 'list'} onClick={() => setView('list')}>
                        List
                     </ViewBtn>
                     <ViewBtn type="button" $active={view === 'cards'} aria-pressed={view === 'cards'} onClick={() => setView('cards')}>
                        Cards
                     </ViewBtn>
                  </ViewToggle>
               </FilterBar>

               {view === 'list' && !partnersLoading && visiblePartners.length > 0 && (
                  <PartnerTable>
                     {visiblePartners.map((partner) => (
                        <PartnerLine key={partner._id} data-testid="partner-line">
                           <LineAvatar>
                              {partner.photoUrl ? (
                                 <img src={partner.photoUrl} alt="" />
                              ) : (
                                 <span>{initialsOf(partner.displayName)}</span>
                              )}
                              {partner.online && <OnlineDot title="Online now" />}
                           </LineAvatar>
                           <LineMain>
                              <LineName>
                                 {partner.displayName}
                                 {partner.online && <OnlineTag>Online</OnlineTag>}
                              </LineName>
                              <LineSub>
                                 {partner.city}, {partner.region}
                              </LineSub>
                           </LineMain>
                           <LineStat>
                              <small>Sold</small>
                              <b>{partner.cumulativeCreditsSold.toLocaleString()}</b>
                           </LineStat>
                           <LineStat>
                              <small>Available</small>
                              <b style={{ color: '#16934a' }}>{partner.availableCredits.toLocaleString()}</b>
                           </LineStat>
                           <LineStat>
                              <small>Per credit</small>
                              <b>₱{partner.baseCreditPrice ?? partner.defaultPricePerCredit}</b>
                           </LineStat>
                           <LineActions>
                              {ownPartnerId === partner._id ? (
                                 <SelfPartnerNote>This is you</SelfPartnerNote>
                              ) : (
                                 <>
                                    {chat.on && partner.user && (
                                       <LineBtn type="button" onClick={() => chat.message(partner.user!)}>
                                          💬 Message
                                       </LineBtn>
                                    )}
                                    <LineBtn type="button" $primary onClick={() => requestCode(partner)}>
                                       Buy →
                                    </LineBtn>
                                    {canApplyReseller && (
                                       <LineLink type="button" onClick={() => setResellerApplyFor(partner)}>
                                          Apply as Reseller
                                       </LineLink>
                                    )}
                                    {myReseller && myReseller.distributor === partner._id && myReseller.status !== 'withdrawn' && (
                                       <ResellerStatusNote $status={myReseller.status}>
                                          {myReseller.status === 'approved'
                                             ? '✓ Your distributor'
                                             : myReseller.status === 'pending'
                                             ? 'Reseller request pending'
                                             : 'Reseller request rejected'}
                                       </ResellerStatusNote>
                                    )}
                                 </>
                              )}
                           </LineActions>
                        </PartnerLine>
                     ))}
                  </PartnerTable>
               )}

               <PartnerList style={view === 'list' && !partnersLoading && visiblePartners.length > 0 ? { display: 'none' } : undefined}>
                  {partnersLoading && (
                     <SkeletonLines lines={3} />
                  )}
                  {!partnersLoading && partners.length === 0 && (
                     <EmptyHint>
                        No active distributors yet. Once admin approves the first
                        applications they'll appear here.
                     </EmptyHint>
                  )}
                  {!partnersLoading &&
                     partners.length > 0 &&
                     visiblePartners.length === 0 && (
                        <EmptyHint>No partners match the selected city.</EmptyHint>
                     )}
                  {visiblePartners.map((partner, idx) => {
                     // 'distributor' is the only rank rendered today — kept as a
                     // styled-component prop so the future tier-based theming
                     // (legendary/diamond/etc.) can hook in without refactor.
                     const rank: Rank = 'distributor';
                     return (
                        <PartnerRow key={partner._id} $rank={rank}>
                           <GlowClip>
                              <RowGlow $rank={rank} />
                           </GlowClip>

                           {idx < 3 && (
                              <LeaderBadge $place={idx + 1}>#{idx + 1}</LeaderBadge>
                           )}

                           <CardHead>
                              <AvatarWrap>
                                 <AvatarRing $rank={rank} />
                                 {partner.photoUrl && (
                                    <AvatarImg
                                       src={partner.photoUrl}
                                       alt={partner.displayName}
                                       onError={(e) => {
                                          (e.target as HTMLImageElement).style.display = 'none';
                                       }}
                                    />
                                 )}
                                 <AvatarFallback>{initialsOf(partner.displayName)}</AvatarFallback>
                              </AvatarWrap>
                              <HeadText>
                                 <PartnerName>
                                    {partner.displayName}
                                    {partner.online && <OnlineTag>Online</OnlineTag>}
                                 </PartnerName>
                                 <PartnerSub>
                                    {partner.city}, {partner.region}
                                 </PartnerSub>
                              </HeadText>
                           </CardHead>

                           {partner.blurb && <PartnerBlurb>{partner.blurb}</PartnerBlurb>}

                           <StatsStrip>
                              <StatBox>
                                 <StatLabel>Sold</StatLabel>
                                 <StatVal>{partner.cumulativeCreditsSold.toLocaleString()}</StatVal>
                              </StatBox>
                              <StatBox $highlight>
                                 <StatLabel>Available</StatLabel>
                                 <StatVal $green>
                                    {partner.availableCredits.toLocaleString()}
                                 </StatVal>
                              </StatBox>
                              <StatBox>
                                 <StatLabel>Per credit</StatLabel>
                                 <StatVal>
                                    ₱{partner.baseCreditPrice ?? partner.defaultPricePerCredit}
                                 </StatVal>
                                 {partner.resellerPricePerCredit != null && (
                                    <StatSubNote>₱{partner.resellerPricePerCredit} resell</StatSubNote>
                                 )}
                              </StatBox>
                           </StatsStrip>

                           <ActionCol>
                              {ownPartnerId === partner._id ? (
                                 <SelfPartnerNote>This is you</SelfPartnerNote>
                              ) : (
                                 <>
                                    <PrimaryActionBtn
                                       $rank={rank}
                                       onClick={() => requestCode(partner)}
                                    >
                                       Inquire / Buy →
                                    </PrimaryActionBtn>
                                    {chat.on && partner.user && (
                                       <ApplyResellerBtn onClick={() => chat.message(partner.user!)}>💬 Message</ApplyResellerBtn>
                                    )}
                                    {canApplyReseller && (
                                       <ApplyResellerBtn
                                          onClick={() => setResellerApplyFor(partner)}
                                       >
                                          Apply as Reseller
                                       </ApplyResellerBtn>
                                    )}
                                    {myReseller &&
                                       myReseller.distributor === partner._id &&
                                       myReseller.status !== 'withdrawn' && (
                                          <>
                                             <ResellerStatusNote
                                                $status={myReseller.status}
                                             >
                                                {myReseller.status === 'approved'
                                                   ? '✓ Your distributor'
                                                   : myReseller.status === 'pending'
                                                   ? 'Reseller request pending'
                                                   : 'Reseller request rejected'}
                                             </ResellerStatusNote>
                                             {myReseller.status === 'pending' &&
                                                (confirmCancelReseller ? (
                                                   <CancelResellerConfirm>
                                                      <span>Cancel this request?</span>
                                                      <MiniLink
                                                         $danger
                                                         disabled={
                                                            cancelResellerMut.isPending
                                                         }
                                                         onClick={() =>
                                                            cancelResellerMut.mutate()
                                                         }
                                                      >
                                                         {cancelResellerMut.isPending
                                                            ? '…'
                                                            : 'Yes, cancel'}
                                                      </MiniLink>
                                                      <MiniLink
                                                         onClick={() =>
                                                            setConfirmCancelReseller(
                                                               false
                                                            )
                                                         }
                                                      >
                                                         Keep
                                                      </MiniLink>
                                                   </CancelResellerConfirm>
                                                ) : (
                                                   <CancelResellerLink
                                                      onClick={() =>
                                                         setConfirmCancelReseller(true)
                                                      }
                                                   >
                                                      Cancel request
                                                   </CancelResellerLink>
                                                ))}
                                          </>
                                       )}
                                 </>
                              )}
                           </ActionCol>
                        </PartnerRow>
                     );
                  })}
               </PartnerList>

               {canApplyDistributor && (
                  <ApplyCta>
                     <ApplyCtaText>
                        <ApplyCtaTitle>
                           {isUpgrade
                              ? 'Ready to become a distributor yourself?'
                              : 'Want to become a SmartFi partner?'}
                        </ApplyCtaTitle>
                        <ApplyCtaSub>
                           {isUpgrade
                              ? 'Buy your credits straight from SmartFi instead of from your distributor. Your credits and points come with you.'
                              : 'Join as a Distributor to access direct credit packages and resell to local owners.'}
                        </ApplyCtaSub>
                     </ApplyCtaText>
                     <ApplyCtaBtn onClick={openApplyModal}>
                        {applyLabel} →
                     </ApplyCtaBtn>
                  </ApplyCta>
               )}
            </>
         )}
      </Wrapper>
   );
};

/* ─── My Inquiries pane (buyer-side history) ───────────────── */

interface MyInquiriesPaneProps {
   inquiries: ApiInquiry[];
   loading: boolean;
   onBrowse: () => void;
}

const MyInquiriesPane = ({ inquiries, loading, onBrowse }: MyInquiriesPaneProps) => {
   const queryClient = useQueryClient();
   const [proofForRow, setProofForRow] = useState<string | null>(null);

   const counts = useMemo(() => {
      const tally: Record<InquiryStatusApi, number> = {
         pending: 0,
         approved: 0,
         fulfilled: 0,
         rejected: 0,
      };
      inquiries.forEach((i) => {
         tally[i.status]++;
      });
      const totalCreditsReceived = inquiries
         .filter((i) => i.status === 'fulfilled')
         .reduce((s, i) => s + i.requestedCredits, 0);
      return { ...tally, totalCreditsReceived };
   }, [inquiries]);

   const sorted = useMemo(
      () => [...inquiries].sort((a, b) => b.createdAt.localeCompare(a.createdAt)),
      [inquiries]
   );

   const submitProofM = useMutation({
      mutationFn: ({ id, url }: { id: string; url: string }) =>
         apiSubmitInquiryProof(id, url),
      onSuccess: () => {
         queryClient.invalidateQueries({ queryKey: ['vendo', 'inquiries'] });
         toast.success('Proof attached', { autoClose: 2000 });
         setProofForRow(null);
      },
      onError: (err: any) => {
         toast.error(err?.response?.data?.error || 'Failed to attach proof');
      },
   });

   return (
      <InqPane>
         {/* Compact (Ray 2026-10-28: "too big, a mess"): one summary line, then one table row per inquiry. */}
         <InqSummary data-testid="inq-summary">
            <InqChip $tone="warn">
               <b>{counts.pending}</b> awaiting distributor
            </InqChip>
            <InqChip $tone="brand">
               <b>{counts.approved}</b> approved · pay these
            </InqChip>
            <InqChip $tone="ok">
               <b>{counts.totalCreditsReceived.toLocaleString()}</b> credits received
            </InqChip>
            {counts.approved > 0 && (
               <InqNudge>
                  💸 Pay the distributor for the approved {counts.approved === 1 ? 'one' : 'ones'} and attach the receipt — the credits land in your wallet once they confirm.
               </InqNudge>
            )}
         </InqSummary>

         {loading && sorted.length === 0 && (
            <InqEmpty>
               <SkeletonLines lines={3} />
            </InqEmpty>
         )}
         {!loading && sorted.length === 0 && (
            <InqEmpty>
               🤝 You haven't sent any inquiries yet.{' '}
               <InqShopBtn type="button" onClick={onBrowse}>
                  Browse partners →
               </InqShopBtn>
            </InqEmpty>
         )}
         {sorted.length > 0 && (
            <InqTable>
               <thead>
                  <tr>
                     <th>Distributor</th>
                     <th className="num">Credits</th>
                     <th className="num">Total</th>
                     <th>Status</th>
                     <th>When</th>
                     <th>Receipt</th>
                  </tr>
               </thead>
               <tbody>
                  {sorted.map((inq) => {
                     const dist = inq.distributor as unknown as PopulatedDistributor;
                     const distName = dist?.displayName || dist?.businessName || 'Unknown distributor';
                     const distLocation = dist?.city && dist?.region ? `${dist.city}, ${dist.region}` : '';
                     const settledPrice = inq.negotiatedPricePerCredit ?? inq.proposedPricePerCredit;
                     const total = inq.requestedCredits * settledPrice;
                     const negotiated =
                        inq.negotiatedPricePerCredit !== undefined && inq.negotiatedPricePerCredit !== inq.proposedPricePerCredit;
                     const when = inq.fulfilledAt ?? inq.approvedAt ?? inq.rejectedAt ?? inq.createdAt;
                     const canAttachProof = inq.status === 'pending' || inq.status === 'approved';
                     const expanded = proofForRow === inq._id;
                     return (
                        <React.Fragment key={inq._id}>
                           <InqTr $status={inq.status} data-focus-id={inq._id}>
                              <td data-label="Distributor">
                                 <InqName>{distName}</InqName>
                                 {distLocation && <InqSub>{distLocation}</InqSub>}
                                 {inq.status !== 'rejected' && inq.notes && <InqSub>“{inq.notes}”</InqSub>}
                                 {inq.status === 'rejected' && inq.notes && <InqReason title={inq.notes}>{inq.notes}</InqReason>}
                              </td>
                              <td className="num" data-label="Credits">
                                 <InqNum>{inq.requestedCredits}</InqNum>
                              </td>
                              <td className="num" data-label="Total">
                                 <InqNum>₱{total.toLocaleString()}</InqNum>
                                 <InqSub>
                                    ₱{settledPrice} × {inq.requestedCredits}
                                    {negotiated ? ` · was ₱${inq.proposedPricePerCredit}` : ''}
                                 </InqSub>
                              </td>
                              <td data-label="Status">
                                 <InqPill $status={inq.status}>{INQUIRY_STATUS_LABEL[inq.status]}</InqPill>
                              </td>
                              <td data-label="When">
                                 <InqSub as="span">{formatRelative(when)}</InqSub>
                              </td>
                              <td data-label="Receipt">
                                 {(inq.paymentProofUrl || canAttachProof) && (
                                    <InqActs>
                                       {inq.paymentProofUrl && (
                                          <InqProofLink href={inq.paymentProofUrl} target="_blank" rel="noopener noreferrer">
                                             📎 View proof
                                          </InqProofLink>
                                       )}
                                       {canAttachProof && (
                                          <InqProofToggle type="button" onClick={() => setProofForRow(expanded ? null : inq._id)}>
                                             {expanded ? 'Cancel' : inq.paymentProofUrl ? 'Replace proof' : 'Attach proof'}
                                          </InqProofToggle>
                                       )}
                                    </InqActs>
                                 )}
                              </td>
                           </InqTr>
                           {expanded && canAttachProof && (
                              <tr>
                                 <td colSpan={6}>
                                    <InqProofPanel>
                                       <InqProofHint>Upload the payment receipt (screenshot or photo). It saves by itself once it finishes uploading.</InqProofHint>
                                       <FileUploader
                                          value=""
                                          onChange={(url) => {
                                             if (url) submitProofM.mutate({ id: inq._id, url });
                                          }}
                                          compact
                                       />
                                    </InqProofPanel>
                                 </td>
                              </tr>
                           )}
                        </React.Fragment>
                     );
                  })}
               </tbody>
            </InqTable>
         )}
      </InqPane>
   );
};

function formatRelative(iso: string): string {
   const diff = Date.now() - new Date(iso).getTime();
   const min = Math.floor(diff / 60000);
   if (min < 1) return 'just now';
   if (min < 60) return `${min}m ago`;
   const hr = Math.floor(min / 60);
   if (hr < 24) return `${hr}h ago`;
   const d = Math.floor(hr / 24);
   return `${d}d ago`;
}

/* ─── animations ────────────────────────────────────────────── */

const goldShine = keyframes`
   0% { background-position: -200% 0; }
   100% { background-position: 200% 0; }
`;

const goldPulse = keyframes`
   0%, 100% { box-shadow: 0 0 0 0 rgba(234, 179, 8, 0.35); }
   50% { box-shadow: 0 0 0 8px rgba(234, 179, 8, 0); }
`;

const silverGlow = keyframes`
   0%, 100% { opacity: 0.6; }
   50% { opacity: 0.9; }
`;

/* ─── styles ────────────────────────────────────────────────── */

const Wrapper = styled.div`
   padding-bottom: 2rem;
`;

/* Pending application card (top of direct/Buy Credits tab) */

const PendingAppCard = styled.div`
   display: flex;
   align-items: center;
   gap: 14px;
   margin: 0 0 18px;
   padding: 14px 18px;
   background:
      linear-gradient(${colors.WHITE}, ${colors.WHITE}) padding-box,
      linear-gradient(135deg, #fde68a, #f59e0b) border-box;
   border: 2px solid transparent;
   border-radius: 12px;
   box-shadow: 0 4px 12px -6px rgba(245, 158, 11, 0.3);
   @media (max-width: 600px) { flex-direction: column; align-items: stretch; }
`;

const PendingAppIcon = styled.span`
   font-size: 32px;
   flex-shrink: 0;
`;

const PendingAppBody = styled.div`
   flex: 1;
   display: flex;
   flex-direction: column;
   gap: 4px;
`;

const PendingAppTitle = styled.p`
   font-size: 14px;
   font-weight: 800;
   color: ${colors.GRAY_DARK};
`;

const PendingAppMeta = styled.p`
   font-size: 12px;
   color: ${colors.GRAY3};
`;

const PendingAppText = styled.p`
   font-size: 11px;
   color: ${colors.GRAY5};
   line-height: 1.4;
`;

const PendingAppScore = styled.div`
   display: flex;
   flex-direction: column;
   align-items: center;
   gap: 2px;
   padding: 8px 14px;
   border-radius: 10px;
   background: ${colors.WHITE};
   border: 1px solid ${colors.GRAY13};
   flex-shrink: 0;
`;

const PendingAppScoreNum = styled.span<{ $score: number }>`
   font-size: 22px;
   font-weight: 900;
   color: ${({ $score }) =>
      $score >= 90 ? colors.SUCCESS : $score >= 75 ? colors.WARNING : colors.GRAY3};
   line-height: 1;
`;

const PendingAppScoreLbl = styled.span`
   font-size: 9px;
   font-weight: 700;
   color: ${colors.GRAY5};
   text-transform: uppercase;
   letter-spacing: 0.06em;
`;

const PartnerBlurb = styled.p`
   font-size: 11px;
   color: ${colors.GRAY3};
   font-style: italic;
   line-height: 1.4;
`;

/* My Inquiries (buyer) */

const InquiryTabBadge = styled.span`
   display: inline-grid;
   place-items: center;
   min-width: 18px;
   height: 18px;
   padding: 0 5px;
   border-radius: 999px;
   background: ${colors.WARNING};
   color: white;
   font-size: 10px;
   font-weight: 800;
   margin-left: 4px;
`;

const InqPane = styled.div`
   padding: 0 1rem;
   @media (max-width: 768px) { padding: 0 12px; }
`;
const InqSummary = styled.div`
   display: flex;
   flex-wrap: wrap;
   align-items: center;
   gap: 8px;
   margin-bottom: 10px;
   font-size: 12.5px;
`;
const chipTone = { warn: colors.WARNING, brand: colors.PRIMARY, ok: colors.SUCCESS } as const;
const InqChip = styled.span<{ $tone: keyof typeof chipTone }>`
   display: inline-flex;
   align-items: center;
   gap: 5px;
   padding: 4px 10px;
   border-radius: 999px;
   border: 1px solid ${({ $tone }) => chipTone[$tone]}55;
   background: ${({ $tone }) => chipTone[$tone]}14;
   color: var(--app-text, ${colors.GRAY1});
   b { color: ${({ $tone }) => chipTone[$tone]}; font-size: 13px; }
`;
const InqNudge = styled.span`
   flex-basis: 100%;
   font-size: 12.5px;
   color: ${colors.GRAY3};
`;
const InqEmpty = styled.div`
   padding: 18px 12px;
   text-align: center;
   font-size: 13px;
   color: ${colors.GRAY3};
   border: 1px dashed ${colors.GRAY13};
   border-radius: 10px;
`;
const InqShopBtn = styled.button`
   border: none;
   background: none;
   padding: 0;
   color: ${colors.PRIMARY};
   font-weight: 700;
   font-size: 13px;
   cursor: pointer;
`;
const InqTable = styled.table`
   width: 100%;
   border-collapse: collapse;
   font-size: 13px;
   th {
      text-align: left;
      font-size: 10.5px;
      font-weight: 700;
      letter-spacing: 0.04em;
      text-transform: uppercase;
      color: ${colors.GRAY3};
      padding: 6px 10px;
      border-bottom: 1px solid ${colors.GRAY13};
      white-space: nowrap;
   }
   td {
      padding: 8px 10px;
      vertical-align: middle;
      border-bottom: 1px solid ${colors.GRAY13};
   }
   .num { text-align: right; white-space: nowrap; }
   @media (max-width: 768px) {
      /* Phone (Ray 2026-10-28): a 3-line card per row — who + status, the numbers, then the actions. No column labels. */
      thead { display: none; }
      tbody tr {
         display: grid;
         /* The first column shrinks (minmax 0) so a long place name wraps instead of pushing the status and time off-screen. */
         grid-template-columns: minmax(0, 1fr) auto auto;
         grid-template-areas: 'who who status' 'credits total when' 'price price price' 'receipt receipt receipt' 'acts acts acts';
         column-gap: 10px;
         row-gap: 3px;
         align-items: center;
         padding: 9px 0 9px 10px;
         border-bottom: 1px solid ${colors.GRAY13};
      }
      td { padding: 0; border: 0; }
      td::before { display: none; }
      td[data-label='Distributor'], td[data-label='Buyer'] { grid-area: who; min-width: 0; }
      td[data-label='Distributor'] p, td[data-label='Buyer'] p { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
      td[data-label='Status'] { grid-area: status; text-align: right; align-self: start; }
      td[data-label='Credits'] { grid-area: credits; text-align: left; }
      td[data-label='Credits'] span::after { content: ' credits'; font-weight: 500; color: ${colors.GRAY3}; }
      td[data-label='Total'] { grid-area: total; text-align: left; }
      td[data-label='Total'] p { display: inline; margin-left: 4px; }
      td[data-label='When'] { grid-area: when; text-align: right; white-space: nowrap; }
      td[data-label='₱ / credit'] { grid-area: price; }
      td[data-label='₱ / credit']::before { content: '₱ per credit: '; display: inline; font-size: 11.5px; color: ${colors.GRAY3}; }
      td[data-label='Receipt'] { grid-area: receipt; }
      td[data-label='Receipt']:empty { display: none; }
      td.acts { grid-area: acts; text-align: left; }
   }
`;
const rowTone: Record<InquiryStatusApi, string> = {
   pending: colors.WARNING,
   approved: colors.PRIMARY,
   fulfilled: colors.SUCCESS,
   rejected: colors.DANGER,
};
const InqTr = styled.tr<{ $status: InquiryStatusApi }>`
   td:first-child { box-shadow: inset 3px 0 0 ${({ $status }) => rowTone[$status]}; }
   @media (max-width: 768px) { box-shadow: inset 3px 0 0 ${({ $status }) => rowTone[$status]}; td:first-child { box-shadow: none; } }
`;
const InqName = styled.p`
   margin: 0;
   font-weight: 700;
   line-height: 1.25;
`;
const InqSub = styled.p`
   margin: 1px 0 0;
   font-size: 11.5px;
   color: ${colors.GRAY3};
   line-height: 1.3;
`;
const InqNum = styled.span`
   display: block;
   font-weight: 700;
   font-variant-numeric: tabular-nums;
`;
const InqPill = styled.span<{ $status: InquiryStatusApi }>`
   display: inline-block;
   padding: 2px 8px;
   border-radius: 999px;
   font-size: 11px;
   font-weight: 700;
   white-space: nowrap;
   color: ${({ $status }) => rowTone[$status]};
   background: ${({ $status }) => rowTone[$status]}1a;
`;
const InqReason = styled.p`
   margin: 2px 0 0;
   max-width: 100%;
   font-size: 11.5px;
   color: ${colors.DANGER};
   line-height: 1.3;
   overflow: hidden;
   text-overflow: ellipsis;
   white-space: nowrap;
`;
const InqActs = styled.div`
   display: flex;
   align-items: center;
   gap: 10px;
   white-space: nowrap;
`;
const InqProofLink = styled.a`
   color: ${colors.PRIMARY};
   font-weight: 700;
   font-size: 12.5px;
   text-decoration: none;
   &:hover { text-decoration: underline; }
`;
const InqProofToggle = styled.button`
   border: 1px solid ${colors.GRAY13};
   background: var(--app-surface, ${colors.WHITE});
   color: var(--app-text, ${colors.GRAY1});
   border-radius: 7px;
   padding: 3px 9px;
   font-size: 12px;
   font-weight: 600;
   cursor: pointer;
   &:hover { border-color: ${colors.PRIMARY}; color: ${colors.PRIMARY}; }
`;
const InqProofPanel = styled.div`
   padding: 8px 10px 10px 13px;
   background: ${colors.GRAY14};
   border-radius: 8px;
`;
const InqProofHint = styled.p`
   margin: 0 0 6px;
   font-size: 12px;
   color: ${colors.GRAY3};
`;

const SelfPartnerNote = styled.span`
   font-size: 10px;
   font-weight: 700;
   color: ${colors.GRAY5};
   padding: 6px 10px;
   border-radius: 8px;
   background: ${colors.GRAY14};
   border: 1px dashed ${colors.GRAY12};
   text-transform: uppercase;
   letter-spacing: 0.06em;
   text-align: center;
`;

const EmptyHint = styled.p`
   padding: 24px 16px;
   text-align: center;
   font-size: 12px;
   color: ${colors.GRAY5};
   background: ${colors.GRAY14};
   border-radius: 10px;
`;

/* Tabs */

const TabBar = styled.div`
   display: flex;
   gap: 8px;
   padding: 0 1rem;
   margin-bottom: 16px;
   border-bottom: 1px solid ${colors.GRAY12};
   @media (max-width: 768px) {
      padding: 0 12px;
      overflow-x: auto;
      flex-wrap: nowrap;
      -webkit-overflow-scrolling: touch;
      scrollbar-width: none;
      &::-webkit-scrollbar { display: none; }
   }
`;

const TabBtn = styled.button<{ $active: boolean }>`
   display: flex;
   align-items: center;
   gap: 8px;
   padding: 10px 16px;
   background: none;
   border: none;
   font-size: 14px;
   font-weight: ${({ $active }) => ($active ? 700 : 500)};
   color: ${({ $active }) => ($active ? colors.PRIMARY : colors.GRAY3)};
   border-bottom: 2px solid ${({ $active }) => ($active ? colors.PRIMARY : 'transparent')};
   cursor: pointer;
   margin-bottom: -1px;
   transition: all 0.15s;
   white-space: nowrap;
   flex-shrink: 0;
   &:hover { color: ${colors.PRIMARY}; }
`;

const TabSavings = styled.span`
   font-size: 10px;
   font-weight: 700;
   background: ${colors.SUCCESS}18;
   color: ${colors.SUCCESS};
   padding: 2px 8px;
   border-radius: 10px;
   text-transform: uppercase;
   letter-spacing: 0.04em;
`;

/* Direct tab */

const DirectPane = styled.div`
   padding: 0 1rem;
   @media (max-width: 768px) { padding: 0 12px; }
`;

const DirectIntro = styled.div`
   text-align: center;
   max-width: 560px;
   margin: 0 auto 20px;
`;

const DirectIntroTitle = styled.h3`
   font-size: 18px;
   font-weight: 800;
   color: ${colors.GRAY_DARK};
   margin-bottom: 6px;
`;

const DirectIntroDesc = styled.p`
   font-size: 13px;
   color: ${colors.GRAY3};
   line-height: 1.6;
`;

const PackageGrid = styled.div`
   display: grid;
   grid-template-columns: repeat(3, 1fr);
   gap: 14px;
   max-width: 1020px;
   margin: 0 auto 20px;
   @media (max-width: 900px) {
      grid-template-columns: repeat(2, 1fr);
   }
   @media (max-width: 520px) {
      grid-template-columns: 1fr;
   }
`;

const tierAccent = (tier: string) => {
   switch (tier) {
      case 'Starter':
         return '#64748b';
      case 'Bronze':
         return '#b45309';
      case 'Silver':
         return '#64748b';
      case 'Gold':
         return '#d97706';
      case 'Diamond':
         return '#0891b2';
      case 'Legendary':
         return '#a21caf';
      default:
         return '#64748b';
   }
};

const tierAccentLight = (tier: string) => {
   switch (tier) {
      case 'Starter':
         return '#cbd5e1';
      case 'Bronze':
         return '#fbbf24';
      case 'Silver':
         return '#e2e8f0';
      case 'Gold':
         return '#fbbf24';
      case 'Diamond':
         return '#22d3ee';
      case 'Legendary':
         return '#e879f9';
      default:
         return '#cbd5e1';
   }
};

const tierTint = (tier: string) => {
   switch (tier) {
      case 'Starter':
         return 'rgba(148, 163, 184, 0.08)';
      case 'Bronze':
         return 'rgba(245, 158, 11, 0.08)';
      case 'Silver':
         return 'rgba(100, 116, 139, 0.08)';
      case 'Gold':
         return 'rgba(245, 158, 11, 0.12)';
      case 'Diamond':
         return 'rgba(6, 182, 212, 0.1)';
      case 'Legendary':
         return 'rgba(192, 38, 211, 0.1)';
      default:
         return 'rgba(148, 163, 184, 0.08)';
   }
};

const packageShineAnim = keyframes`
   0% { transform: translateX(-100%) skewX(-20deg); }
   100% { transform: translateX(300%) skewX(-20deg); }
`;

const floaty = keyframes`
   0%, 100% { transform: translateY(0); }
   50% { transform: translateY(-6px); }
`;

const PackageCard = styled.div<{ $featured: boolean; $tier: string }>`
   position: relative;
   background:
      linear-gradient(${colors.WHITE}, ${colors.WHITE}) padding-box,
      linear-gradient(135deg,
         ${({ $tier }) => tierAccentLight($tier)},
         ${({ $tier }) => tierAccent($tier)},
         ${({ $tier }) => tierAccentLight($tier)}
      ) border-box;
   border: 2px solid transparent;
   border-radius: 16px;
   padding: 18px 14px 14px;
   display: flex;
   flex-direction: column;
   align-items: center;
   gap: 4px;
   text-align: center;
   color: ${colors.GRAY_DARK};
   overflow: hidden;
   transition: transform 0.2s, box-shadow 0.2s;
   box-shadow: 0 3px 12px -4px ${({ $tier }) => `${tierAccent($tier)}25`};
   ${({ $featured, $tier }) =>
      $featured &&
      css`
         transform: scale(1.04);
         box-shadow: 0 10px 28px -8px ${tierAccent($tier)}60;
      `}
   &:hover {
      transform: ${({ $featured }) => ($featured ? 'scale(1.06)' : 'translateY(-3px)')};
      box-shadow: 0 8px 20px -6px ${({ $tier }) => `${tierAccent($tier)}50`};
   }
`;

const PackageShine = styled.div<{ $tier: string }>`
   position: absolute;
   top: 0;
   left: 0;
   width: 100%;
   height: 100%;
   background: linear-gradient(
      180deg,
      ${({ $tier }) => tierTint($tier)} 0%,
      transparent 40%
   );
   pointer-events: none;
   &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 40%;
      height: 100%;
      background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.6), transparent);
      animation: ${packageShineAnim} 5s ease-in-out infinite;
   }
`;

const BestValueRibbon = styled.span`
   position: absolute;
   top: 10px;
   right: -28px;
   transform: rotate(35deg);
   background: linear-gradient(135deg, #f43f5e, #ec4899);
   color: white;
   font-size: 9px;
   font-weight: 900;
   padding: 4px 30px;
   letter-spacing: 0.08em;
   text-transform: uppercase;
   z-index: 3;
   box-shadow: 0 2px 8px rgba(236, 72, 153, 0.4);
`;

const PackageIconWrap = styled.div<{ $tier: string }>`
   position: relative;
   width: 64px;
   height: 64px;
   display: grid;
   place-items: center;
   margin-bottom: 2px;
   animation: ${floaty} 3s ease-in-out infinite;
`;

const PackageIcon = styled.span`
   font-size: 42px;
   position: relative;
   z-index: 1;
   filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3));
`;

const PackageIconAura = styled.div<{ $tier: string }>`
   position: absolute;
   inset: -6px;
   border-radius: 50%;
   background: radial-gradient(circle, ${({ $tier }) => tierAccent($tier)}55, transparent 70%);
   filter: blur(10px);
`;

const PackageTag = styled.span<{ $tier?: string }>`
   font-size: 11px;
   font-weight: 900;
   color: ${({ $tier }) => ($tier ? tierAccent($tier) : colors.GRAY3)};
   text-transform: uppercase;
   letter-spacing: 0.15em;
   position: relative;
   z-index: 2;
`;

const PackageHeader = styled.div`
   display: flex;
   align-items: baseline;
   gap: 4px;
   margin-top: 2px;
   position: relative;
   z-index: 2;
`;

const PackageCreditsNum = styled.span`
   font-size: 36px;
   font-weight: 900;
   color: ${colors.GRAY_DARK};
   line-height: 1;
   letter-spacing: -0.02em;
`;

const PackageCreditsLbl = styled.span`
   font-size: 12px;
   color: ${colors.GRAY5};
   font-weight: 600;
`;

const PackagePrice = styled.p`
   font-size: 20px;
   font-weight: 800;
   color: ${colors.GRAY_DARK};
   margin-top: 6px;
   position: relative;
   z-index: 2;
`;

const PackagePerCredit = styled.div`
   font-size: 11px;
   color: ${colors.GRAY5};
   margin-bottom: 10px;
   display: flex;
   flex-direction: column;
   align-items: center;
   gap: 3px;
   position: relative;
   z-index: 2;
`;

const PackageSavings = styled.span`
   font-size: 10px;
   font-weight: 800;
   color: ${colors.SUCCESS};
   background: ${colors.SUCCESS}15;
   padding: 2px 8px;
   border-radius: 10px;
   text-transform: uppercase;
   letter-spacing: 0.05em;
`;

const PackageLockedPrice = styled.div`
   font-size: 12px;
   font-weight: 700;
   color: ${colors.GRAY5};
   background: ${colors.GRAY14};
   border: 1px dashed ${colors.GRAY12};
   border-radius: 10px;
   padding: 8px 12px;
   margin: 6px 0 10px;
   position: relative;
   z-index: 2;
`;

const PackageBuyBtn = styled.button<{ $featured: boolean; $tier?: string }>`
   ${({ $featured, $tier }) =>
      $featured
         ? css`
              background: linear-gradient(
                 135deg,
                 ${$tier ? tierAccent($tier) : colors.PRIMARY},
                 ${$tier ? tierAccentLight($tier) : colors.PRIMARY}
              );
              color: white;
              border: none;
              box-shadow: 0 4px 12px ${$tier ? `${tierAccent($tier)}50` : 'rgba(108, 92, 231, 0.3)'};
           `
         : css`
              background: ${colors.WHITE};
              color: ${$tier ? tierAccent($tier) : colors.PRIMARY};
              border: 1.5px solid ${$tier ? tierAccent($tier) : colors.PRIMARY};
           `}
   padding: 9px 18px;
   border-radius: 10px;
   font-size: 11px;
   font-weight: 800;
   letter-spacing: 0.04em;
   text-transform: uppercase;
   cursor: pointer;
   width: 100%;
   margin-top: 4px;
   transition: all 0.15s;
   position: relative;
   z-index: 2;
   &:hover {
      transform: translateY(-1px);
      opacity: 0.92;
   }
   &:disabled {
      cursor: not-allowed;
      opacity: 0.5;
      filter: grayscale(0.4);
      transform: none;
      box-shadow: none;
   }
`;

const SwitchHint = styled.p`
   font-size: 12px;
   color: ${colors.GRAY3};
   text-align: center;
   max-width: 540px;
   margin: 0 auto;
   padding: 12px;
   background: ${colors.SUCCESS}08;
   border-radius: 8px;
`;

const SwitchLink = styled.span`
   color: ${colors.PRIMARY};
   font-weight: 700;
   cursor: pointer;
   &:hover { text-decoration: underline; }
`;

// Compact 2-column header: intro copy (left) + credit-release panel (right),
// so the listing isn't pushed down half the screen.
const PartnersTop = styled.div`
   display: grid;
   grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
   gap: 16px;
   align-items: stretch;
   padding: 0 1rem;
   margin-bottom: 16px;
   @media (max-width: 900px) { grid-template-columns: 1fr; }
   @media (max-width: 768px) { padding: 0 12px; }
`;

const Intro = styled.div`
   display: flex;
   flex-direction: column;
   justify-content: center;
`;

const IntroTitle = styled.h2`
   font-size: 16px;
   font-weight: 700;
   color: ${colors.GRAY_DARK};
   margin-bottom: 4px;
`;

const IntroText = styled.p`
   font-size: 13px;
   color: ${colors.GRAY3};
   line-height: 1.5;
   max-width: 640px;
`;

const StatsRow = styled.div`
   display: flex;
   gap: 10px;
   padding: 0 1rem;
   margin-bottom: 18px;
   flex-wrap: wrap;
   @media (max-width: 768px) { padding: 0 12px; }
`;

type ReleaseTone = 'ok' | 'warn' | 'danger';

function toneFromHeadroom(available: number, limit: number): ReleaseTone {
   if (limit <= 0) return 'ok';
   const ratio = available / limit;
   if (ratio <= 0.15) return 'danger';
   if (ratio <= 0.35) return 'warn';
   return 'ok';
}

const toneColor = (tone: ReleaseTone) =>
   tone === 'danger'
      ? colors.DANGER
      : tone === 'warn'
      ? colors.WARNING
      : colors.SUCCESS;

const ReleasePanel = styled.div`
   margin: 0;
   padding: 12px 14px;
   border: 1px solid ${colors.GRAY13};
   background: ${colors.WHITE};
   border-radius: 12px;
   display: flex;
   flex-direction: column;
   gap: 10px;
`;

const ReleaseHeader = styled.div`
   display: flex;
   flex-direction: column;
   gap: 2px;
`;

const ReleaseTitle = styled.p`
   font-size: 12.5px;
   font-weight: 700;
   color: ${colors.MAIN_DARK};
   line-height: 1.45;
`;

const ReleaseHint = styled.p`
   font-size: 11px;
   color: ${colors.GRAY5};
`;

const ReleaseStats = styled.div`
   display: grid;
   grid-template-columns: 1fr 1fr;
   gap: 10px;
   @media (max-width: 480px) { grid-template-columns: 1fr; }
`;

const ReleaseStat = styled.div`
   padding: 10px 12px;
   border: 1px solid ${colors.GRAY13};
   border-radius: 8px;
   background: ${colors.GRAY14};
`;

const ReleaseLbl = styled.p`
   font-size: 10px;
   font-weight: 700;
   color: ${colors.GRAY5};
   text-transform: uppercase;
   letter-spacing: 0.05em;
`;

const ReleaseVal = styled.p<{ $tone: ReleaseTone }>`
   font-size: 24px;
   font-weight: 900;
   color: ${({ $tone }) => toneColor($tone)};
   line-height: 1.1;
   margin-top: 2px;
`;

const ReleaseSub = styled.p`
   font-size: 11px;
   color: ${colors.GRAY5};
   margin-top: 2px;
`;

const ReleaseBarTrack = styled.div`
   width: 100%;
   height: 8px;
   border-radius: 999px;
   background: ${colors.GRAY13};
   overflow: hidden;
`;

const ReleaseBarFill = styled.div<{ $pct: number; $tone: ReleaseTone }>`
   width: ${({ $pct }) => $pct}%;
   height: 100%;
   background: ${({ $tone }) => toneColor($tone)};
   transition: width 400ms ease;
`;

const ReleaseBarLbl = styled.p`
   font-size: 11px;
   color: ${colors.GRAY5};
   text-align: center;
`;

const FilterBar = styled.div`
   display: flex;
   gap: 10px;
   padding: 0 1rem;
   margin-bottom: 14px;
   align-items: flex-end;
   flex-wrap: wrap;
   @media (max-width: 768px) { padding: 0 12px; }
`;

const FilterGroup = styled.div`
   display: flex;
   flex-direction: column;
   gap: 4px;
`;

const FilterLbl = styled.label`
   font-size: 10px;
   font-weight: 700;
   color: ${colors.GRAY5};
   text-transform: uppercase;
   letter-spacing: 0.06em;
`;

const FilterSelect = styled.select`
   padding: 7px 10px;
   border: 1px solid ${colors.GRAY12};
   border-radius: 6px;
   font-size: 13px;
   font-family: inherit;
   color: ${colors.MAIN_DARK};
   background: ${colors.WHITE};
   cursor: pointer;
   min-width: 160px;
   &:focus { border-color: ${colors.PRIMARY}; outline: none; }
`;

const FilterCount = styled.span`
   margin-left: auto;
   font-size: 11px;
   color: ${colors.GRAY5};
   padding-bottom: 8px;
`;

const StatChip = styled.div`
   flex: 1;
   min-width: 120px;
   background: ${colors.WHITE};
   border: 1px solid ${colors.GRAY13};
   border-radius: 10px;
   padding: 10px 14px;
`;

const StatChipVal = styled.p`
   font-size: 20px;
   font-weight: 800;
   color: ${colors.GRAY_DARK};
   line-height: 1.1;
`;

const StatChipLbl = styled.p`
   font-size: 11px;
   color: ${colors.GRAY5};
   margin-top: 2px;
`;

const FilterRow = styled.div`
   display: flex;
   gap: 6px;
   padding: 0 1rem;
   margin-bottom: 16px;
   flex-wrap: wrap;
   @media (max-width: 768px) { padding: 0 12px; }
`;

const FilterChip = styled.button<{ $active: boolean }>`
   background: ${({ $active }) => ($active ? colors.PRIMARY : colors.GRAY14)};
   color: ${({ $active }) => ($active ? 'white' : colors.GRAY3)};
   border: 1px solid ${({ $active }) => ($active ? colors.PRIMARY : colors.GRAY12)};
   padding: 5px 14px;
   border-radius: 20px;
   font-size: 12px;
   font-weight: 600;
   cursor: pointer;
`;

const Grid = styled.div`
   display: grid;
   grid-template-columns: repeat(3, 1fr);
   gap: 14px;
   padding: 0 1rem;
   @media (max-width: 1024px) { grid-template-columns: repeat(2, 1fr); }
   @media (max-width: 640px) { grid-template-columns: 1fr; padding: 0 12px; }
`;

/* ── Partner row list (gaming vibe, one partner per row) ── */

const PartnerList = styled.div`
   display: grid;
   grid-template-columns: repeat(3, minmax(0, 1fr));
   gap: 14px;
   padding: 0 1rem;
   @media (max-width: 1100px) { grid-template-columns: repeat(2, minmax(0, 1fr)); }
   @media (max-width: 680px) { grid-template-columns: minmax(0, 1fr); }
   @media (max-width: 768px) { padding: 0 12px; }
`;

const rowSkin = (rank: Rank) => {
   if (rank === 'distributor') {
      return css`
         background:
            linear-gradient(${colors.WHITE}, ${colors.WHITE}) padding-box,
            linear-gradient(135deg, #fde68a, #f59e0b, #fbbf24, #d97706) border-box;
         border: 2px solid transparent;
         box-shadow: 0 4px 14px -6px rgba(245, 158, 11, 0.3);
      `;
   }
   if (rank === 'seller') {
      return css`
         background: ${colors.WHITE};
         border: 2px solid #cbd5e1;
         box-shadow: 0 2px 8px -3px rgba(100, 116, 139, 0.15);
      `;
   }
   return css`
      background: ${colors.WHITE};
      border: 2px solid #d6ad5e;
      box-shadow: 0 2px 6px -2px rgba(161, 98, 7, 0.18);
   `;
};

const PartnerRow = styled.div<{ $rank: Rank }>`
   position: relative;
   display: flex;
   flex-direction: column;
   gap: 10px;
   padding: 14px;
   border-radius: 14px;
   color: ${colors.GRAY_DARK};
   ${({ $rank }) => rowSkin($rank)};
   transition: transform 0.15s ease;
   &:hover { transform: translateY(-1px); }
   @media (max-width: 768px) {
      grid-template-columns: 1fr;
      gap: 10px;
      padding: 10px 12px;
   }
`;

const GlowClip = styled.div`
   position: absolute;
   inset: 0;
   border-radius: inherit;
   overflow: hidden;
   pointer-events: none;
   z-index: 0;
`;

const RowGlow = styled.div<{ $rank: Rank }>`
   position: absolute;
   top: 50%;
   left: 0;
   width: 40%;
   height: 140%;
   transform: translateY(-50%);
   pointer-events: none;
   opacity: 0.55;
   filter: blur(40px);
   background: ${({ $rank }) =>
      $rank === 'distributor'
         ? 'radial-gradient(circle, rgba(251, 191, 36, 0.35), transparent 70%)'
         : $rank === 'seller'
         ? 'radial-gradient(circle, rgba(148, 163, 184, 0.22), transparent 70%)'
         : 'radial-gradient(circle, rgba(161, 98, 7, 0.22), transparent 70%)'};
`;

const AvatarWrap = styled.div`
   position: relative;
   width: 56px;
   height: 56px;
   flex-shrink: 0;
   z-index: 1;
   align-self: start;
`;

const AvatarRing = styled.div<{ $rank: Rank }>`
   position: absolute;
   inset: -3px;
   border-radius: 50%;
   padding: 2px;
   background: ${({ $rank }) =>
      $rank === 'distributor'
         ? 'conic-gradient(from 0deg, #f59e0b, #fbbf24, #fde68a, #f59e0b, #d97706, #f59e0b)'
         : $rank === 'seller'
         ? 'conic-gradient(from 0deg, #cbd5e1, #94a3b8, #e2e8f0, #cbd5e1)'
         : 'conic-gradient(from 0deg, #a16207, #ca8a04, #eab308, #a16207)'};
   ${({ $rank }) =>
      $rank === 'distributor' &&
      css`animation: ${goldPulse} 3s ease-out infinite;`}
`;

const AvatarImg = styled.img`
   position: relative;
   width: 100%;
   height: 100%;
   border-radius: 50%;
   object-fit: cover;
   z-index: 2;
   background: ${colors.GRAY14};
`;

const AvatarFallback = styled.span`
   position: absolute;
   inset: 0;
   display: grid;
   place-items: center;
   font-size: 16px;
   font-weight: 800;
   color: white;
   z-index: 1;
   background: ${colors.GRAY5};
   border-radius: 50%;
`;

const RankMedallion = styled.div<{ $rank: Rank }>`
   position: absolute;
   bottom: -2px;
   right: -2px;
   width: 20px;
   height: 20px;
   border-radius: 50%;
   display: grid;
   place-items: center;
   font-size: 10px;
   font-weight: 900;
   z-index: 3;
   border: 2px solid ${colors.WHITE};
   background: ${({ $rank }) =>
      $rank === 'distributor'
         ? 'linear-gradient(135deg, #f59e0b, #fbbf24)'
         : $rank === 'seller'
         ? 'linear-gradient(135deg, #94a3b8, #cbd5e1)'
         : 'linear-gradient(135deg, #a16207, #ca8a04)'};
   color: ${({ $rank }) =>
      $rank === 'distributor' ? '#422006' : $rank === 'seller' ? '#1e293b' : '#fef3c7'};
`;

const CardHead = styled.div`
   position: relative;
   z-index: 1;
   display: flex;
   align-items: center;
   gap: 10px;
   min-width: 0;
`;

const HeadText = styled.div`
   min-width: 0;
   display: flex;
   flex-direction: column;
   gap: 1px;
`;

const InfoCol = styled.div`
   position: relative;
   z-index: 1;
   display: flex;
   flex-direction: column;
   gap: 3px;
   min-width: 0;
`;

const InfoTopRow = styled.div`
   display: flex;
   align-items: center;
   gap: 8px;
   flex-wrap: wrap;
`;

const RankChip = styled.span<{ $rank: Rank }>`
   padding: 2px 8px;
   border-radius: 8px;
   font-size: 9px;
   font-weight: 900;
   letter-spacing: 0.08em;
   ${({ $rank }) => {
      if ($rank === 'distributor') {
         return css`
            background: linear-gradient(135deg, #f59e0b, #fbbf24);
            color: #422006;
         `;
      }
      if ($rank === 'seller') {
         return css`
            background: linear-gradient(135deg, #94a3b8, #cbd5e1);
            color: #1e293b;
         `;
      }
      return css`
         background: linear-gradient(135deg, #a16207, #ca8a04);
         color: #fef3c7;
      `;
   }};
`;

const StatsStrip = styled.div`
   display: grid;
   grid-template-columns: repeat(3, 1fr);
   gap: 6px;
   margin-top: 4px;
   @media (max-width: 480px) {
      grid-template-columns: 1fr 1fr;
   }
`;

const StatBox = styled.div<{ $highlight?: boolean }>`
   padding: 5px 8px;
   border-radius: 6px;
   background: ${({ $highlight }) => ($highlight ? `${colors.SUCCESS}10` : colors.GRAY14)};
   border: 1px solid
      ${({ $highlight }) => ($highlight ? `${colors.SUCCESS}40` : colors.GRAY12)};
`;

const StatLabel = styled.p`
   font-size: 9px;
   color: ${colors.GRAY5};
   text-transform: uppercase;
   letter-spacing: 0.06em;
   font-weight: 700;
`;

const StatVal = styled.p<{ $green?: boolean }>`
   font-size: 13px;
   font-weight: 800;
   color: ${({ $green }) => ($green ? colors.SUCCESS : colors.GRAY_DARK)};
   margin-top: 1px;
`;

const StatSubNote = styled.p`
   font-size: 10px;
   font-weight: 700;
   color: ${colors.PRIMARY};
   margin-top: 1px;
`;

const ActionCol = styled.div`
   display: flex;
   flex-direction: column;
   align-items: stretch;
   justify-content: flex-end;
   gap: 6px;
   position: relative;
   z-index: 1;
   margin-top: auto;
`;

/* Rank-based card skins */

const rankSkin = (rank: Rank) => {
   if (rank === 'distributor') {
      return css`
         border: 2px solid transparent;
         background:
            linear-gradient(${colors.WHITE}, ${colors.WHITE}) padding-box,
            linear-gradient(135deg, #fde68a, #f59e0b, #fbbf24, #f59e0b) border-box;
         box-shadow: 0 8px 24px -8px rgba(245, 158, 11, 0.35);
         position: relative;
         overflow: hidden;
         &::before {
            content: '';
            position: absolute;
            inset: 0;
            background: linear-gradient(110deg, transparent 40%, rgba(255, 223, 120, 0.15) 50%, transparent 60%);
            background-size: 200% 100%;
            animation: ${goldShine} 6s ease-in-out infinite;
            pointer-events: none;
         }
      `;
   }
   if (rank === 'seller') {
      return css`
         border: 2px solid #cbd5e1;
         background: linear-gradient(155deg, #f8fafc 0%, ${colors.WHITE} 100%);
         box-shadow: 0 4px 16px -6px rgba(100, 116, 139, 0.2);
      `;
   }
   return css`
      border: 2px solid ${colors.GRAY12};
      background: ${colors.WHITE};
   `;
};

const PartnerCard = styled.div<{ $rank: Rank }>`
   position: relative;
   border-radius: 14px;
   padding: 16px;
   display: flex;
   flex-direction: column;
   gap: 12px;
   ${({ $rank }) => rankSkin($rank)};
`;

const RankRibbon = styled.span<{ $rank: Rank }>`
   position: absolute;
   top: 12px;
   right: 12px;
   padding: 3px 10px;
   border-radius: 10px;
   font-size: 10px;
   font-weight: 800;
   letter-spacing: 0.08em;
   z-index: 2;
   ${({ $rank }) => {
      if ($rank === 'distributor') {
         return css`
            background: linear-gradient(135deg, #f59e0b, #fbbf24);
            color: #422006;
            box-shadow: 0 2px 8px rgba(245, 158, 11, 0.4);
         `;
      }
      if ($rank === 'seller') {
         return css`
            background: linear-gradient(135deg, #94a3b8, #cbd5e1);
            color: #1e293b;
         `;
      }
      return css`
         background: linear-gradient(135deg, #a16207, #ca8a04);
         color: #fef3c7;
      `;
   }};
`;

const LeaderBadge = styled.div<{ $place: number }>`
   position: absolute;
   top: -9px;
   left: -6px;
   width: 26px;
   height: 26px;
   border-radius: 50%;
   display: grid;
   place-items: center;
   font-size: 11px;
   font-weight: 900;
   color: white;
   z-index: 4;
   border: 2px solid ${colors.WHITE};
   background: ${({ $place }) =>
      $place === 1 ? '#f59e0b' : $place === 2 ? '#94a3b8' : '#a16207'};
   box-shadow: 0 3px 8px rgba(0, 0, 0, 0.2);
`;

const CardHeader = styled.div`
   display: flex;
   gap: 12px;
   align-items: flex-start;
   position: relative;
   z-index: 1;
`;

const Avatar = styled.div<{ $rank: Rank }>`
   width: 52px;
   height: 52px;
   border-radius: 14px;
   display: grid;
   place-items: center;
   position: relative;
   flex-shrink: 0;
   background: ${({ $rank }) =>
      $rank === 'distributor'
         ? 'linear-gradient(135deg, #f59e0b, #fbbf24)'
         : $rank === 'seller'
         ? 'linear-gradient(135deg, #94a3b8, #cbd5e1)'
         : 'linear-gradient(135deg, #a16207, #ca8a04)'};
   ${({ $rank }) => $rank === 'distributor' && css`animation: ${goldPulse} 2.5s ease-out infinite;`};
`;

const AvatarAura = styled.div<{ $rank: Rank }>`
   position: absolute;
   inset: -6px;
   border-radius: 18px;
   pointer-events: none;
   ${({ $rank }) => {
      if ($rank === 'distributor') {
         return css`
            background: radial-gradient(circle, rgba(251, 191, 36, 0.4), transparent 70%);
            filter: blur(8px);
         `;
      }
      if ($rank === 'seller') {
         return css`
            background: radial-gradient(circle, rgba(203, 213, 225, 0.4), transparent 70%);
            filter: blur(6px);
            animation: ${silverGlow} 3s ease-in-out infinite;
         `;
      }
      return css`display: none;`;
   }};
`;

const AvatarText = styled.span`
   font-size: 18px;
   font-weight: 800;
   color: white;
   position: relative;
   z-index: 1;
   letter-spacing: -0.02em;
`;

const HeaderText = styled.div`
   flex: 1;
   min-width: 0;
   padding-right: 40px; /* leave space for ribbon */
`;

const PartnerName = styled.p`
   font-size: 14px;
   font-weight: 800;
   color: ${colors.GRAY_DARK};
   line-height: 1.2;
   letter-spacing: -0.01em;
`;

const PartnerSub = styled.p`
   font-size: 11px;
   color: ${colors.GRAY5};
`;

const PartnerRating = styled.div`
   font-size: 12px;
   color: #f59e0b;
   letter-spacing: 1px;
   display: flex;
   align-items: center;
   gap: 4px;
`;

const RatingNum = styled.span`
   font-size: 11px;
   color: ${colors.GRAY3};
   margin-left: 3px;
   font-weight: 700;
   letter-spacing: 0;
`;

const SinceText = styled.span`
   font-size: 10px;
   color: ${colors.GRAY5};
   margin-left: 2px;
   letter-spacing: 0;
`;

const RankTagline = styled.p<{ $rank: Rank }>`
   font-size: 11px;
   font-weight: 600;
   color: ${({ $rank }) =>
      $rank === 'distributor' ? '#92400e' : $rank === 'seller' ? '#475569' : '#78350f'};
   font-style: italic;
   position: relative;
   z-index: 1;
`;

const InventoryRow = styled.div`
   display: grid;
   grid-template-columns: repeat(4, 1fr);
   gap: 6px;
   padding: 10px;
   border-radius: 10px;
   background: ${colors.GRAY14};
   position: relative;
   z-index: 1;
`;

const InventoryCell = styled.div``;

const InvLabel = styled.p`
   font-size: 9px;
   color: ${colors.GRAY5};
   text-transform: uppercase;
   letter-spacing: 0.04em;
   font-weight: 700;
`;

const InvValue = styled.p<{ $green?: boolean }>`
   font-size: 13px;
   font-weight: 800;
   color: ${({ $green }) => ($green ? colors.SUCCESS : colors.GRAY_DARK)};
   margin-top: 2px;
`;

const ContactRow = styled.div`
   display: flex;
   gap: 4px;
   flex-wrap: wrap;
   position: relative;
   z-index: 1;
   margin-top: 4px;
`;

const ContactBtn = styled.button`
   background: ${colors.GRAY14};
   border: 1px solid ${colors.GRAY12};
   padding: 3px 8px;
   border-radius: 5px;
   font-size: 10px;
   font-weight: 600;
   color: ${colors.GRAY1};
   cursor: pointer;
   transition: all 0.15s;
   &:hover {
      background: ${colors.GRAY13};
      border-color: ${colors.GRAY10};
   }
`;

const PrimaryActionBtn = styled.button<{ $rank: Rank }>`
   width: 100%;
   padding: 9px 14px;
   border-radius: 8px;
   border: none;
   font-size: 11px;
   font-weight: 800;
   letter-spacing: 0.04em;
   cursor: pointer;
   position: relative;
   z-index: 1;
   color: white;
   text-transform: uppercase;
   background: ${({ $rank }) =>
      $rank === 'distributor'
         ? 'linear-gradient(135deg, #f59e0b, #fbbf24, #f59e0b)'
         : $rank === 'seller'
         ? 'linear-gradient(135deg, #64748b, #94a3b8, #64748b)'
         : 'linear-gradient(135deg, #a16207, #ca8a04, #a16207)'};
   background-size: 200% 100%;
   transition: all 0.2s;
   box-shadow: ${({ $rank }) =>
      $rank === 'distributor'
         ? '0 3px 10px rgba(245, 158, 11, 0.3)'
         : $rank === 'seller'
         ? '0 2px 8px rgba(148, 163, 184, 0.2)'
         : '0 2px 8px rgba(161, 98, 7, 0.25)'};
   &:hover:not(:disabled) {
      background-position: 100% 0;
   }
   &:disabled {
      background: ${colors.GRAY12};
      color: ${colors.GRAY5};
      cursor: not-allowed;
      box-shadow: none;
   }
   @media (max-width: 768px) {
      width: 100%;
   }
`;

const ApplyCta = styled.div`
   display: flex;
   align-items: center;
   justify-content: space-between;
   gap: 16px;
   margin: 28px 1rem 8px;
   padding: 16px 20px;
   background: ${colors.PRIMARY}0c;
   border: 1px solid ${colors.PRIMARY}30;
   border-radius: 12px;
   @media (max-width: 700px) {
      flex-direction: column;
      align-items: stretch;
      text-align: center;
      margin: 24px 12px 8px;
   }
`;

const ApplyCtaText = styled.div`
   display: flex;
   flex-direction: column;
   gap: 4px;
   min-width: 0;
`;

const ApplyCtaTitle = styled.p`
   font-size: 15px;
   font-weight: 800;
   color: ${colors.GRAY_DARK};
`;

const ApplyCtaSub = styled.p`
   font-size: 12px;
   color: ${colors.GRAY3};
   line-height: 1.4;
`;

const ApplyCtaBtn = styled.button`
   flex-shrink: 0;
   background: ${colors.PRIMARY};
   color: white;
   border: none;
   padding: 10px 18px;
   border-radius: 10px;
   font-size: 13px;
   font-weight: 700;
   cursor: pointer;
   white-space: nowrap;
   &:hover { opacity: 0.92; }
`;

export default Partners;

/* ─── My Points pane (loyalty e-wallet + history) ──────────────────── */

const POINTS_PER_CREDIT = 500; // display-only conversion (phase 1)

const POINTS_REASON_LABEL: Record<string, string> = {
   'distributor-purchase': 'Bundle purchase',
   'reseller-purchase': 'Reseller purchase',
   referral: 'Referral bonus',
   'admin-adjust': 'Adjustment',
   redemption: 'Redeemed',
};

const MyPointsPane: React.FC = () => {
   const { data, isLoading } = useQuery({
      queryKey: ['vendo', 'my-points'],
      queryFn: getMyPointsApi,
   });
   const balance = data?.balance ?? 0;
   const ledger: ApiPointsLedgerRow[] = data?.ledger ?? [];

   // Held credit balance — shown as its own card.
   const { data: creditData } = useQuery({
      queryKey: ['vendo', 'credits'],
      queryFn: getCredits,
   });
   const heldCredits = creditData?.credits ?? 0;
   const usedCredits = creditData?.used ?? 0;

   const { handleShow } = useUIStore();
   const [showTransfer, setShowTransfer] = useState(false);
   const [subTab, setSubTab] = useState<'overview' | 'history'>('overview');
   const pointsPerCredit = data?.mechanics?.pointsPerCredit ?? POINTS_PER_CREDIT;
   const maxConvertible = Math.floor(balance / pointsPerCredit);
   const openConvert = () =>
      handleShow({
         template: 'convert-points',
         title: 'Convert points to credits',
         windowType: 'desktop',
         data: { balance, pointsPerCredit },
      });

   // Activity roll-ups from the (positive) ledger deltas.
   const now = new Date();
   const startOfToday = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate()
   ).getTime();
   const startOfYesterday = startOfToday - 86_400_000;
   const startOfWeek = startOfToday - 6 * 86_400_000;
   let earnedToday = 0;
   let earnedYesterday = 0;
   let earnedWeek = 0;
   for (const r of ledger) {
      if (r.delta <= 0) continue;
      const t = new Date(r.createdAt).getTime();
      if (t >= startOfToday) earnedToday += r.delta;
      else if (t >= startOfYesterday) earnedYesterday += r.delta;
      if (t >= startOfWeek) earnedWeek += r.delta;
   }

   return (
      <PointsWrap>
         <SubTabBar>
            <SubTabBtn
               $active={subTab === 'overview'}
               onClick={() => setSubTab('overview')}
            >
               Overview
            </SubTabBtn>
            <SubTabBtn
               $active={subTab === 'history'}
               onClick={() => setSubTab('history')}
            >
               History
            </SubTabBtn>
         </SubTabBar>

         {subTab === 'overview' && (
            <>
               <PointsTopGrid>
                  <PointsCard>
                     <PointsCardLabel>Your Points</PointsCardLabel>
                     <PointsBalanceRow>
                        <PointsBalance>{balance.toLocaleString()}</PointsBalance>
                        <ConvertCtaBtn
                           type="button"
                           onClick={openConvert}
                           disabled={maxConvertible < 1}
                        >
                           Convert to credits
                           <ConvertCtaSub>
                              {maxConvertible < 1
                                 ? 'not enough yet'
                                 : `up to ${maxConvertible.toLocaleString()} credit${
                                      maxConvertible === 1 ? '' : 's'
                                   }`}
                           </ConvertCtaSub>
                        </ConvertCtaBtn>
                     </PointsBalanceRow>
                     <PointsCardHint>
                        ✨ {pointsPerCredit.toLocaleString()} pts = 1 credit
                     </PointsCardHint>
                     <PointsMiniStats>
                        <PointsMiniStat>
                           <PointsMiniVal>+{earnedToday.toLocaleString()}</PointsMiniVal>
                           <PointsMiniLbl>Today</PointsMiniLbl>
                        </PointsMiniStat>
                        <PointsMiniStat>
                           <PointsMiniVal>
                              +{earnedYesterday.toLocaleString()}
                           </PointsMiniVal>
                           <PointsMiniLbl>Yesterday</PointsMiniLbl>
                        </PointsMiniStat>
                        <PointsMiniStat>
                           <PointsMiniVal>+{earnedWeek.toLocaleString()}</PointsMiniVal>
                           <PointsMiniLbl>This week</PointsMiniLbl>
                        </PointsMiniStat>
                     </PointsMiniStats>
                  </PointsCard>

                  <CreditsOnHandCard>
                     <PointsCardLabel>Credits on hand</PointsCardLabel>
                     <CreditsOnHandVal>
                        🎟️ {heldCredits.toLocaleString()}
                        <CreditsUsedNote>· {usedCredits.toLocaleString()} used</CreditsUsedNote>
                     </CreditsOnHandVal>
                     <PointsCardHint>
                        1 credit = 1 vendo device, lifetime license.
                     </PointsCardHint>
                     <TransferCreditBtn
                        type="button"
                        onClick={() => setShowTransfer(true)}
                        disabled={heldCredits < 1}
                     >
                        ↗ Transfer credit
                     </TransferCreditBtn>
                  </CreditsOnHandCard>

                  <ReferralCard />
               </PointsTopGrid>

               <PointsMechanicsCard mechanics={data?.mechanics} />

               <RewardsSection balance={balance} />
            </>
         )}

         {subTab === 'history' && (
            <HistoryTab ledger={ledger} isLoading={isLoading} />
         )}

         {showTransfer && (
            <TransferCreditModal
               balance={heldCredits}
               onClose={() => setShowTransfer(false)}
            />
         )}
      </PointsWrap>
   );
};

/* ─── History tab: points history + redemptions, two columns, paginated ─ */

const HISTORY_PAGE_SIZE = 8;

const HistoryTab: React.FC<{
   ledger: ApiPointsLedgerRow[];
   isLoading: boolean;
}> = ({ ledger, isLoading }) => {
   const { data: redData } = useQuery({
      queryKey: ['vendo', 'my-redemptions'],
      queryFn: listMyRedemptionsApi,
   });
   const redemptions: ApiRedemption[] = redData?.redemptions ?? [];
   const [histPage, setHistPage] = useState(1);
   const [redPage, setRedPage] = useState(1);

   return (
      <>
      <HistoryCols>
         <HistoryCol>
            <PointsLogsTitle>Points history</PointsLogsTitle>
            {isLoading ? (
               <SkeletonLines lines={3} />
            ) : ledger.length === 0 ? (
               <PointsEmpty>No points activity yet. 🎁</PointsEmpty>
            ) : (
               <PagedTable
                  rows={ledger}
                  page={histPage}
                  onPage={setHistPage}
                  head={
                     <>
                        <HCell>Activity</HCell>
                        <HCell $right>Points</HCell>
                        <HCell $right>Balance</HCell>
                     </>
                  }
                  renderRow={(row: ApiPointsLedgerRow) => (
                     <HRow key={row._id}>
                        <HCell>
                           <HReason>
                              {POINTS_REASON_LABEL[row.reason] ?? row.reason}
                           </HReason>
                           {row.note && <HSub>{row.note}</HSub>}
                           <HSub>{formatRelativeDate(row.createdAt)}</HSub>
                        </HCell>
                        <HCellRight $color={row.delta >= 0 ? colors.SUCCESS : colors.DANGER}>
                           {row.delta >= 0 ? '+' : ''}
                           {row.delta.toLocaleString()}
                        </HCellRight>
                        <HCellRight>{row.balanceAfter.toLocaleString()}</HCellRight>
                     </HRow>
                  )}
               />
            )}
         </HistoryCol>

         <HistoryCol>
            <PointsLogsTitle>Redemptions</PointsLogsTitle>
            {redemptions.length === 0 ? (
               <PointsEmpty>No redemptions yet.</PointsEmpty>
            ) : (
               <PagedTable
                  rows={redemptions}
                  page={redPage}
                  onPage={setRedPage}
                  head={
                     <>
                        <HCell>Reward</HCell>
                        <HCell $right>Points</HCell>
                        <HCell $right>Status</HCell>
                     </>
                  }
                  renderRow={(r: ApiRedemption) => (
                     <HRow key={r._id}>
                        <HCell>
                           <HReason>{r.incentiveName}</HReason>
                           <HSub>{formatRelativeDate(r.createdAt)}</HSub>
                        </HCell>
                        <HCellRight>{r.pointsCost.toLocaleString()}</HCellRight>
                        <HCellRight>
                           <RedemptionStatusPill $status={r.status}>
                              {REDEMPTION_STATUS_LABEL[r.status] ?? r.status}
                           </RedemptionStatusPill>
                        </HCellRight>
                     </HRow>
                  )}
               />
            )}
         </HistoryCol>
      </HistoryCols>

      <div>
         <PointsLogsTitle>Credit transfers</PointsLogsTitle>
         <CreditTransfersTable />
      </div>
      </>
   );
};

// Generic paginated table body. `rows` is the full list; it slices per page.
function PagedTable<T>({
   rows,
   page,
   onPage,
   head,
   renderRow,
}: {
   rows: T[];
   page: number;
   onPage: (p: number) => void;
   head: React.ReactNode;
   renderRow: (row: T) => React.ReactNode;
}) {
   const { pageRows, totalPages, safePage } = paginate(
      rows,
      page,
      HISTORY_PAGE_SIZE
   );
   return (
      <>
         <HTable>
            <HHead>{head}</HHead>
            {pageRows.map((r) => renderRow(r))}
         </HTable>
         {totalPages > 1 && (
            <Pager>
               <PagerBtn
                  type="button"
                  disabled={safePage <= 1}
                  onClick={() => onPage(safePage - 1)}
               >
                  ← Prev
               </PagerBtn>
               <PagerInfo>
                  {safePage} / {totalPages}
               </PagerInfo>
               <PagerBtn
                  type="button"
                  disabled={safePage >= totalPages}
                  onClick={() => onPage(safePage + 1)}
               >
                  Next →
               </PagerBtn>
            </Pager>
         )}
      </>
   );
}

/* ─── Convert points → credits card (500 pts = 1 credit) ───────────── */

/* ─── Redemption status labels (used by the History tab) ───────────── */

const REDEMPTION_STATUS_LABEL: Record<string, string> = {
   pending: '⏳ Pending',
   fulfilled: '✓ Fulfilled',
   rejected: '✕ Rejected (refunded)',
};

/* ─── How points work + referral eligibility ──────────────────────── */

const PointsMechanicsCard: React.FC<{ mechanics?: ApiPointsMechanics }> = ({
   mechanics,
}) => {
   const pointsPerCredit = mechanics?.pointsPerCredit ?? POINTS_PER_CREDIT;
   const distributorRate = mechanics?.distributorPointsPerCredit ?? 50;
   const resellerRate = mechanics?.resellerPointsPerCredit ?? 50;
   const referralRate = mechanics?.referralPointsPerCredit ?? 10;
   return (
      <MechWrap>
         <PointsLogsTitle>How points work</PointsLogsTitle>
         <MechGrid>
            <MechCol>
               <MechColTitle>🎁 Earn points</MechColTitle>
               <MechRow>
                  <MechIcon>🛒</MechIcon>
                  <div>
                     <MechRowTitle>Distributor</MechRowTitle>
                     <MechRowSub>
                        <strong>{distributorRate} pts</strong> per credit you buy
                        from SmartFi, plus any bundle bonus — see{' '}
                        <strong>Buy Credits</strong>.
                     </MechRowSub>
                  </div>
               </MechRow>
               <MechRow>
                  <MechIcon>🤝</MechIcon>
                  <div>
                     <MechRowTitle>Reseller</MechRowTitle>
                     <MechRowSub>
                        <strong>{resellerRate} pts</strong> per credit you buy from
                        your distributor.
                     </MechRowSub>
                  </div>
               </MechRow>
               <MechRow>
                  <MechIcon>📣</MechIcon>
                  <div>
                     <MechRowTitle>Referral</MechRowTitle>
                     <MechRowSub>
                        <strong>{referralRate} pts</strong> per credit your invited
                        friend buys from SmartFi.
                     </MechRowSub>
                  </div>
               </MechRow>
            </MechCol>

            <MechCol>
               <MechColTitle>💸 Spend points</MechColTitle>
               <MechRow>
                  <MechIcon>💱</MechIcon>
                  <div>
                     <MechRowTitle>Convert to credits</MechRowTitle>
                     <MechRowSub>
                        <strong>{pointsPerCredit.toLocaleString()} pts = 1 credit.</strong>{' '}
                        Use the Convert card above.
                     </MechRowSub>
                  </div>
               </MechRow>
               <MechRow>
                  <MechIcon>🎉</MechIcon>
                  <div>
                     <MechRowTitle>Redeem rewards</MechRowTitle>
                     <MechRowSub>
                        Trade points for the rewards below once you have enough.
                     </MechRowSub>
                  </div>
               </MechRow>
            </MechCol>
         </MechGrid>

         <MechEligibility>
            <strong>📣 Referral eligibility:</strong> share your invite link (in the
            card above). You earn referral points <strong>every time</strong> someone
            you invited buys credits from SmartFi — including their repeat purchases,
            for as long as they keep buying. They must become an approved
            distributor first (only real credit purchases from SmartFi count, so it
            can’t be gamed by dummy accounts).
         </MechEligibility>
      </MechWrap>
   );
};

const MechWrap = styled.div`
   display: flex;
   flex-direction: column;
   gap: 10px;
`;
const MechGrid = styled.div`
   display: grid;
   grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
   gap: 14px;
`;
const MechCol = styled.div`
   background: ${colors.WHITE};
   border: 1px solid ${colors.GRAY12};
   border-radius: 12px;
   padding: 16px 18px;
   display: flex;
   flex-direction: column;
   gap: 12px;
`;
const MechColTitle = styled.div`
   font-size: 13px;
   font-weight: 800;
   color: ${colors.MAIN_DARK};
`;
const MechRow = styled.div`
   display: flex;
   gap: 12px;
   align-items: flex-start;
`;
const MechIcon = styled.div`
   font-size: 22px;
   line-height: 1.2;
   flex-shrink: 0;
`;
const MechRowTitle = styled.div`
   font-size: 13px;
   font-weight: 700;
   color: ${colors.MAIN_DARK};
`;
const MechRowSub = styled.div`
   font-size: 12px;
   color: ${colors.GRAY5};
   line-height: 1.45;
   margin-top: 2px;
`;
const MechEligibility = styled.div`
   font-size: 12px;
   color: ${colors.GRAY3};
   line-height: 1.55;
   padding: 12px 14px;
   background: ${colors.PRIMARY}10;
   border: 1px solid ${colors.PRIMARY}30;
   border-radius: 10px;
`;

/* ─── Incentives catalog (progress toward rewards) ─────────────────── */

const INCENTIVE_ICON: Record<string, string> = {
   credit: '🎟️',
   cash: '💵',
   gadget: '📱',
   appliance: '🔌',
   travel: '✈️',
};

const RewardsSection: React.FC<{ balance: number }> = ({ balance }) => {
   const qc = useQueryClient();
   const { data } = useQuery({
      queryKey: ['vendo', 'incentives'],
      queryFn: listIncentivesApi,
   });
   const [redeeming, setRedeeming] = useState<ApiIncentive | null>(null);
   const incentives: ApiIncentive[] = data?.incentives ?? [];
   if (incentives.length === 0) return null;
   return (
      <div>
         <PointsLogsTitle style={{ marginTop: 18 }}>Rewards 🎁</PointsLogsTitle>
         <IncentiveGrid>
            {incentives.map((inc) => {
               const pct = Math.min(
                  100,
                  inc.pointsCost > 0
                     ? Math.round((balance / inc.pointsCost) * 100)
                     : 0
               );
               const toGo = Math.max(0, inc.pointsCost - balance);
               const canRedeem = balance >= inc.pointsCost && inc.pointsCost > 0;
               return (
                  <IncentiveCard key={inc._id}>
                     {inc.imageUrl ? (
                        <IncentivePhoto src={inc.imageUrl} alt={inc.name} />
                     ) : (
                        <IncentivePhotoFallback>
                           {INCENTIVE_ICON[inc.category ?? ''] ?? '🎁'}
                        </IncentivePhotoFallback>
                     )}
                     <IncentiveBody>
                        <IncentiveName>{inc.name}</IncentiveName>
                        <IncentiveCost>
                           {inc.pointsCost.toLocaleString()} pts
                        </IncentiveCost>
                        {inc.description && (
                           <IncentiveDesc>{inc.description}</IncentiveDesc>
                        )}
                        <IncentiveBarTrack>
                           <IncentiveBarFill
                              style={{ width: `${pct}%` }}
                              $done={pct >= 100}
                           />
                        </IncentiveBarTrack>
                        <IncentiveHint $done={pct >= 100}>
                           {pct >= 100
                              ? '✓ Ready to redeem!'
                              : `${pct}% — ${toGo.toLocaleString()} points to go`}
                        </IncentiveHint>
                        <IncentiveRedeemBtn
                           disabled={!canRedeem}
                           onClick={() => setRedeeming(inc)}
                        >
                           {canRedeem ? 'Redeem' : 'Not enough points'}
                        </IncentiveRedeemBtn>
                     </IncentiveBody>
                  </IncentiveCard>
               );
            })}
         </IncentiveGrid>
         {redeeming && (
            <RedeemModal
               incentive={redeeming}
               onClose={() => setRedeeming(null)}
               onDone={() => {
                  qc.invalidateQueries({ queryKey: ['vendo', 'my-points'] });
                  qc.invalidateQueries({ queryKey: ['vendo', 'my-redemptions'] });
                  setRedeeming(null);
               }}
            />
         )}
      </div>
   );
};

const RedeemModal: React.FC<{
   incentive: ApiIncentive;
   onClose: () => void;
   onDone: () => void;
}> = ({ incentive, onClose, onDone }) => {
   const [note, setNote] = useState('');
   const redeem = useMutation({
      mutationFn: () => redeemIncentiveApi(incentive._id, note.trim() || undefined),
      onSuccess: () => {
         toast.success('Redemption requested! We will contact you to deliver it. 🎁');
         onDone();
      },
      onError: (e: any) =>
         toast.error(e?.response?.data?.error || 'Could not redeem'),
   });
   return (
      <RModalOverlay onClick={onClose}>
         <RModalCard onClick={(e) => e.stopPropagation()}>
            <RModalTitle>Redeem reward</RModalTitle>
            <RModalSub>
               Redeem <strong>{incentive.name}</strong> for{' '}
               <strong>{incentive.pointsCost.toLocaleString()} points</strong>. The
               points are deducted now and an admin will deliver your reward.
            </RModalSub>
            <RModalLabel>Delivery / contact note (optional)</RModalLabel>
            <RModalTextarea
               value={note}
               onChange={(e) => setNote(e.target.value)}
               rows={3}
               placeholder="Address, GCash number, or how to reach you"
            />
            <RModalFooter>
               <RModalCancel onClick={onClose} disabled={redeem.isPending}>
                  Cancel
               </RModalCancel>
               <RModalSubmit
                  disabled={redeem.isPending}
                  onClick={() => redeem.mutate()}
               >
                  {redeem.isPending ? 'Redeeming…' : 'Confirm redeem'}
               </RModalSubmit>
            </RModalFooter>
         </RModalCard>
      </RModalOverlay>
   );
};

const IncentiveGrid = styled.div`
   display: grid;
   grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
   gap: 14px;
   margin-top: 10px;
`;
const IncentiveCard = styled.div`
   background: ${colors.WHITE};
   border: 1px solid ${colors.GRAY12};
   border-radius: 12px;
   overflow: hidden;
   display: flex;
   flex-direction: column;
`;
const IncentivePhoto = styled.img`
   width: 100%;
   height: 130px;
   object-fit: cover;
   display: block;
`;
const IncentivePhotoFallback = styled.div`
   width: 100%;
   height: 130px;
   display: flex;
   align-items: center;
   justify-content: center;
   font-size: 48px;
   background: ${colors.GRAY14};
`;
const IncentiveBody = styled.div`
   padding: 14px 16px;
   display: flex;
   flex-direction: column;
   flex: 1;
`;
const IncentiveDesc = styled.div`
   font-size: 12px;
   color: ${colors.GRAY5};
   margin: 6px 0 10px;
   line-height: 1.4;
`;
const IncentiveRedeemBtn = styled.button`
   margin-top: 12px;
   font-family: inherit;
   font-size: 13px;
   font-weight: 700;
   padding: 9px 14px;
   border-radius: 8px;
   border: none;
   background: ${colors.PRIMARY};
   color: ${colors.WHITE};
   cursor: pointer;
   &:disabled {
      background: ${colors.GRAY14};
      color: ${colors.GRAY5};
      cursor: default;
   }
`;
const IncentiveName = styled.div`
   font-size: 14px;
   font-weight: 700;
   color: ${colors.MAIN_DARK};
`;
const IncentiveCost = styled.div`
   font-size: 12px;
   color: ${colors.GRAY5};
`;
const IncentiveBarTrack = styled.div`
   height: 10px;
   background: ${colors.GRAY14};
   border-radius: 999px;
   overflow: hidden;
`;
const IncentiveBarFill = styled.div<{ $done: boolean }>`
   height: 100%;
   border-radius: 999px;
   transition: width 0.4s ease;
   background: ${({ $done }) =>
      $done
         ? colors.SUCCESS
         : `linear-gradient(90deg, ${colors.PRIMARY}, #f59e0b)`};
`;
const IncentiveHint = styled.div<{ $done: boolean }>`
   margin-top: 7px;
   font-size: 12px;
   font-weight: 600;
   color: ${({ $done }) => ($done ? colors.SUCCESS : colors.GRAY3)};
`;

const PointsWrap = styled.div`
   display: flex;
   flex-direction: column;
   gap: 18px;
   width: 100%;
`;
const SubTabBar = styled.div`
   display: flex;
   gap: 6px;
   border-bottom: 1px solid ${colors.GRAY12};
`;
const SubTabBtn = styled.button<{ $active: boolean }>`
   font-family: inherit;
   font-size: 14px;
   font-weight: 700;
   padding: 8px 16px;
   border: none;
   background: none;
   cursor: pointer;
   color: ${({ $active }) => ($active ? colors.PRIMARY : colors.GRAY3)};
   border-bottom: 2px solid
      ${({ $active }) => ($active ? colors.PRIMARY : 'transparent')};
   margin-bottom: -1px;
`;
const ConvertCtaBtn = styled.button`
   display: flex;
   flex-direction: column;
   align-items: center;
   gap: 1px;
   padding: 8px 14px;
   border: none;
   border-radius: 10px;
   background: rgba(255, 255, 255, 0.18);
   color: ${colors.WHITE};
   font-family: inherit;
   font-size: 13px;
   font-weight: 800;
   cursor: pointer;
   white-space: nowrap;
   transition: background 0.15s;
   &:hover:not(:disabled) { background: rgba(255, 255, 255, 0.28); }
   &:disabled { opacity: 0.5; cursor: default; }
`;
const ConvertCtaSub = styled.span`
   font-size: 11px;
   font-weight: 600;
   opacity: 0.9;
`;
const CreditsOnHandCard = styled.div`
   background: ${colors.WHITE};
   border: 1px solid ${colors.GRAY12};
   border-radius: 14px;
   padding: 18px;
   display: flex;
   flex-direction: column;
   gap: 4px;
`;
const CreditsOnHandVal = styled.div`
   font-size: 40px;
   font-weight: 900;
   line-height: 1.1;
   color: ${colors.SUCCESS};
   display: flex;
   align-items: baseline;
   gap: 8px;
   flex-wrap: wrap;
`;
const CreditsUsedNote = styled.span`
   font-size: 13px;
   font-weight: 600;
   color: ${colors.GRAY5};
`;
const TransferCreditBtn = styled.button`
   margin-top: auto;
   align-self: flex-start;
   font-family: inherit;
   font-size: 13px;
   font-weight: 700;
   padding: 8px 14px;
   border: 1px solid ${colors.PRIMARY};
   border-radius: 9px;
   background: transparent;
   color: ${colors.PRIMARY};
   cursor: pointer;
   transition: background 0.15s;
   &:hover:not(:disabled) { background: ${colors.PRIMARY}0d; }
   &:disabled { opacity: 0.5; cursor: default; }
`;
const HistoryCols = styled.div`
   display: grid;
   grid-template-columns: 1fr 1fr;
   gap: 16px;
   @media (max-width: 768px) { grid-template-columns: 1fr; }
`;
const HistoryCol = styled.div`
   display: flex;
   flex-direction: column;
   gap: 8px;
   min-width: 0;
`;
const HTable = styled.div`
   display: flex;
   flex-direction: column;
   border: 1px solid ${colors.GRAY12};
   border-radius: 10px;
   overflow: hidden;
`;
const HHead = styled.div`
   display: grid;
   grid-template-columns: 1fr auto auto;
   gap: 10px;
   padding: 9px 12px;
   background: ${colors.GRAY14};
   border-bottom: 1px solid ${colors.GRAY13};
   font-size: 10px;
   font-weight: 800;
   text-transform: uppercase;
   letter-spacing: 0.04em;
   color: ${colors.GRAY5};
`;
const HRow = styled.div`
   display: grid;
   grid-template-columns: 1fr auto auto;
   gap: 10px;
   padding: 10px 12px;
   align-items: center;
   border-bottom: 1px solid ${colors.GRAY14};
   font-size: 13px;
   &:last-child { border-bottom: none; }
`;
const HCell = styled.div<{ $right?: boolean }>`
   min-width: 0;
   text-align: ${({ $right }) => ($right ? 'right' : 'left')};
   word-break: break-word;
`;
const HCellRight = styled.div<{ $color?: string }>`
   text-align: right;
   font-weight: 800;
   white-space: nowrap;
   color: ${({ $color }) => $color ?? colors.MAIN_DARK};
`;
const HReason = styled.div`
   font-weight: 700;
   color: ${colors.MAIN_DARK};
`;
const HSub = styled.div`
   font-size: 11px;
   color: ${colors.GRAY5};
   word-break: break-word;
`;
const Pager = styled.div`
   display: flex;
   align-items: center;
   justify-content: flex-end;
   gap: 10px;
   padding-top: 4px;
`;
const PagerBtn = styled.button`
   font-family: inherit;
   font-size: 12px;
   font-weight: 700;
   padding: 5px 10px;
   border: 1px solid ${colors.GRAY12};
   border-radius: 7px;
   background: ${colors.WHITE};
   color: ${colors.GRAY3};
   cursor: pointer;
   &:disabled { opacity: 0.4; cursor: default; }
`;
const PagerInfo = styled.span`
   font-size: 12px;
   color: ${colors.GRAY5};
   font-weight: 700;
`;
const PointsTopGrid = styled.div`
   display: grid;
   grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
   gap: 14px;
   align-items: stretch;
`;
const PointsCard = styled.div`
   background: linear-gradient(135deg, ${colors.PRIMARY}, #6d28d9);
   color: ${colors.WHITE};
   border-radius: 16px;
   padding: 22px 24px;
   box-shadow: 0 10px 30px rgba(109, 40, 217, 0.25);
   display: flex;
   flex-direction: column;
   justify-content: center;
`;
const ConvertCardBox = styled.div`
   background: ${colors.WHITE};
   border: 1px solid ${colors.GRAY12};
   border-radius: 16px;
   padding: 18px 20px;
   display: flex;
   flex-direction: column;
   gap: 10px;
`;
const ConvertRow = styled.div`
   display: flex;
   align-items: center;
   gap: 8px;
`;
const ConvertStepper = styled.button`
   width: 38px;
   height: 38px;
   border-radius: 8px;
   border: 1px solid ${colors.GRAY12};
   background: ${colors.WHITE};
   color: ${colors.MAIN_DARK};
   font-size: 20px;
   font-weight: 700;
   cursor: pointer;
   flex-shrink: 0;
   &:disabled {
      opacity: 0.4;
      cursor: default;
   }
`;
const ConvertInput = styled.input`
   flex: 1;
   width: 100%;
   text-align: center;
   font-family: inherit;
   font-size: 18px;
   font-weight: 700;
   padding: 8px;
   border: 1px solid ${colors.GRAY12};
   border-radius: 8px;
   box-sizing: border-box;
   &:focus {
      outline: none;
      border-color: ${colors.PRIMARY};
   }
`;
const ConvertHint = styled.div`
   font-size: 12px;
   color: ${colors.GRAY5};
`;
const ConvertBtn = styled.button`
   font-family: inherit;
   font-size: 13px;
   font-weight: 700;
   padding: 10px 14px;
   border-radius: 8px;
   border: none;
   background: ${colors.PRIMARY};
   color: ${colors.WHITE};
   cursor: pointer;
   &:disabled {
      background: ${colors.GRAY14};
      color: ${colors.GRAY5};
      cursor: default;
   }
`;
const RedemptionStatusPill = styled.span<{ $status: string }>`
   font-size: 11px;
   font-weight: 700;
   padding: 4px 10px;
   border-radius: 999px;
   white-space: nowrap;
   color: ${({ $status }) =>
      $status === 'fulfilled'
         ? colors.SUCCESS
         : $status === 'rejected'
         ? colors.GRAY5
         : colors.WARNING ?? '#b45309'};
   background: ${colors.GRAY14};
`;
const PointsCardLabel = styled.div`
   font-size: 12px;
   font-weight: 700;
   text-transform: uppercase;
   letter-spacing: 0.06em;
   opacity: 0.85;
`;
const PointsBalanceRow = styled.div`
   display: flex;
   align-items: baseline;
   justify-content: space-between;
   gap: 12px;
   flex-wrap: wrap;
`;
const PointsBalance = styled.div`
   font-size: 44px;
   font-weight: 800;
   line-height: 1.1;
   margin: 6px 0;
`;
const PointsHeldCredits = styled.div`
   display: flex;
   flex-direction: column;
   align-items: flex-end;
   font-size: 22px;
   font-weight: 800;
   line-height: 1.1;
   opacity: 0.95;
`;
const PointsHeldCreditsLbl = styled.span`
   font-size: 11px;
   font-weight: 600;
   opacity: 0.85;
`;
const PointsCardHint = styled.div`
   font-size: 13px;
   opacity: 0.9;
`;
const PointsMiniStats = styled.div`
   display: flex;
   gap: 10px;
   margin-top: 16px;
   padding-top: 14px;
   border-top: 1px solid rgba(255, 255, 255, 0.22);
`;
const PointsMiniStat = styled.div`
   flex: 1;
   text-align: center;
`;
const PointsMiniVal = styled.div`
   font-size: 16px;
   font-weight: 800;
   line-height: 1.1;
`;
const PointsMiniLbl = styled.div`
   font-size: 10px;
   text-transform: uppercase;
   letter-spacing: 0.04em;
   opacity: 0.8;
   margin-top: 3px;
`;
const PointsLogsTitle = styled.h3`
   margin: 0;
   font-size: 14px;
   font-weight: 700;
   color: ${colors.MAIN_DARK};
`;
const PointsEmpty = styled.div`
   font-size: 13px;
   color: ${colors.GRAY5};
   padding: 16px;
   background: ${colors.GRAY14};
   border-radius: 10px;
`;
const PointsLogGrid = styled.div`
   display: flex;
   flex-direction: column;
   gap: 8px;
   margin-top: 8px;
`;
const PointsLogRow = styled.div`
   display: flex;
   justify-content: space-between;
   align-items: center;
   padding: 10px 14px;
   background: ${colors.WHITE};
   border: 1px solid ${colors.GRAY12};
   border-radius: 10px;
`;
const PointsLogInfo = styled.div`
   display: flex;
   flex-direction: column;
   gap: 2px;
   min-width: 0;
`;
const PointsLogReason = styled.div`
   font-size: 13px;
   font-weight: 600;
   color: ${colors.MAIN_DARK};
`;
const PointsLogNote = styled.div`
   font-size: 11px;
   color: ${colors.GRAY3};
   word-break: break-word;
`;
const PointsLogDate = styled.div`
   font-size: 11px;
   color: ${colors.GRAY5};
`;
const PointsLogRight = styled.div`
   text-align: right;
`;
const PointsLogDelta = styled.div<{ $positive: boolean }>`
   font-size: 15px;
   font-weight: 800;
   color: ${({ $positive }) => ($positive ? colors.SUCCESS : colors.DANGER)};
`;
const PointsLogBalance = styled.div`
   font-size: 11px;
   color: ${colors.GRAY5};
`;

const PointsEarnWrap = styled.div`
   margin-top: 8px;
   display: flex;
   flex-direction: column;
   align-items: center;
   gap: 6px;
`;
const PointsEarnBadge = styled.div`
   font-size: 11px;
   font-weight: 800;
   color: ${colors.WHITE};
   background: linear-gradient(135deg, #f59e0b, #ef4444);
   padding: 5px 12px;
   border-radius: 999px;
   box-shadow: 0 2px 8px rgba(245, 158, 11, 0.4);
   white-space: nowrap;
`;
const PointsBonusFlag = styled.div`
   font-size: 10px;
   font-weight: 900;
   color: #fff;
   background: #b91c1c;
   padding: 3px 10px;
   border-radius: 999px;
   letter-spacing: 0.03em;
   white-space: nowrap;
   box-shadow: 0 2px 6px rgba(185, 28, 28, 0.35);
   animation: ${keyframes`
      0%, 100% { transform: scale(1); }
      50% { transform: scale(1.08); }
   `} 1.4s ease-in-out infinite;
`;

/* ─── Apply-as-reseller modal + row controls ───────────────────────── */

const RESELLER_CHECKLIST: { key: string; label: string }[] = [
   { key: 'ownDevice', label: 'I have my own piso WiFi / vendo to sell on' },
   { key: 'sellLocally', label: 'I will resell credits in my own area' },
   { key: 'validId', label: 'The government ID I uploaded is mine and valid' },
   { key: 'agreePricing', label: "I agree to my distributor's reseller pricing" },
];

const ResellerApplyModal: React.FC<{
   partner: ApiPartner;
   onClose: () => void;
}> = ({ partner, onClose }) => {
   const qc = useQueryClient();
   const [displayName, setDisplayName] = useState('');
   const [contactPhone, setContactPhone] = useState('');
   const [facebookUrl, setFacebookUrl] = useState('');
   const [note, setNote] = useState('');
   const [govIdUrl, setGovIdUrl] = useState('');
   const [checklist, setChecklist] = useState<Record<string, boolean>>({});
   const allChecked = RESELLER_CHECKLIST.every((c) => checklist[c.key]);
   const mutation = useMutation({
      mutationFn: () =>
         applyResellerApi({
            distributorId: partner._id,
            displayName: displayName.trim(),
            contactPhone: contactPhone.trim() || undefined,
            facebookUrl: facebookUrl.trim() || undefined,
            note: note.trim() || undefined,
            govIdUrl: govIdUrl || undefined,
            checklist,
         }),
      onSuccess: () => {
         toast.success('Reseller application sent — the distributor will review it.');
         qc.invalidateQueries({ queryKey: ['vendo', 'my-reseller'] });
         onClose();
      },
      onError: (e: any) =>
         toast.error(e?.response?.data?.error || 'Could not send application'),
   });
   const valid =
      displayName.trim().length > 0 && govIdUrl.length > 0 && allChecked;
   const distName =
      (partner as any).displayName || (partner as any).businessName || 'this distributor';
   return (
      <RModalOverlay onClick={onClose}>
         <RModalCardWide onClick={(e) => e.stopPropagation()}>
            <RModalTitle>Apply as reseller</RModalTitle>
            <RModalSub>
               under <strong>{distName}</strong> — buy credits from them at reseller price and earn points.
            </RModalSub>
            <RModalCols>
               <div>
                  <RModalLabel>Your display name *</RModalLabel>
                  <RModalInput
                     value={displayName}
                     onChange={(e) => setDisplayName(e.target.value)}
                     placeholder="Juan Dela Cruz"
                  />
                  {!displayName.trim() && (
                     <RModalErr>Required</RModalErr>
                  )}
                  <RModalLabel>Contact number</RModalLabel>
                  <RModalInput
                     value={contactPhone}
                     onChange={(e) => setContactPhone(e.target.value)}
                     placeholder="09xx xxx xxxx"
                  />
                  <RModalLabel>Facebook profile URL</RModalLabel>
                  <RModalInput
                     value={facebookUrl}
                     onChange={(e) => setFacebookUrl(e.target.value)}
                     placeholder="https://facebook.com/your.profile"
                  />
                  <RModalLabel>Note (area you cover, why)</RModalLabel>
                  <RModalTextarea
                     value={note}
                     onChange={(e) => setNote(e.target.value)}
                     rows={3}
                  />
               </div>
               <div>
                  <RModalLabel>Government ID photo *</RModalLabel>
                  <FileUploader
                     value={govIdUrl}
                     onChange={setGovIdUrl}
                     accept="image/*"
                     compact
                  />
                  {!govIdUrl && <RModalErr>Please upload a valid ID</RModalErr>}
                  <RModalLabel>Qualifying checklist *</RModalLabel>
                  <ChecklistBox>
                     {RESELLER_CHECKLIST.map((c) => (
                        <ChecklistItem key={c.key}>
                           <input
                              type="checkbox"
                              checked={!!checklist[c.key]}
                              onChange={(e) =>
                                 setChecklist((prev) => ({
                                    ...prev,
                                    [c.key]: e.target.checked,
                                 }))
                              }
                           />
                           <span>{c.label}</span>
                        </ChecklistItem>
                     ))}
                  </ChecklistBox>
                  {!allChecked && (
                     <RModalErr>Please confirm all items</RModalErr>
                  )}
               </div>
            </RModalCols>
            <RModalFooter>
               <RModalCancel onClick={onClose} disabled={mutation.isPending}>
                  Cancel
               </RModalCancel>
               <RModalSubmit
                  disabled={!valid || mutation.isPending}
                  onClick={() => mutation.mutate()}
               >
                  {mutation.isPending ? 'Sending…' : 'Send application'}
               </RModalSubmit>
            </RModalFooter>
         </RModalCardWide>
      </RModalOverlay>
   );
};

const RModalCardWide = styled.div`
   background: ${colors.WHITE};
   border-radius: 14px;
   padding: 22px;
   width: 100%;
   max-width: 720px;
   max-height: 90vh;
   overflow-y: auto;
   box-shadow: 0 20px 50px rgba(0, 0, 0, 0.3);
`;
const RModalCols = styled.div`
   display: grid;
   grid-template-columns: 1fr 1fr;
   gap: 18px;
   @media (max-width: 600px) {
      grid-template-columns: 1fr;
   }
`;
const RModalErr = styled.div`
   font-size: 11px;
   font-weight: 600;
   color: ${colors.DANGER ?? '#dc2626'};
   margin-top: 4px;
`;
const ChecklistBox = styled.div`
   display: flex;
   flex-direction: column;
   gap: 8px;
   margin-top: 4px;
`;
const ChecklistItem = styled.label`
   display: flex;
   align-items: flex-start;
   gap: 8px;
   font-size: 13px;
   color: ${colors.GRAY3};
   cursor: pointer;
   line-height: 1.4;
   input {
      margin-top: 2px;
      flex-shrink: 0;
   }
`;

const ApplyResellerBtn = styled.button`
   margin-top: 8px;
   font-family: inherit;
   font-size: 12px;
   font-weight: 700;
   padding: 8px 14px;
   border-radius: 8px;
   border: 1.5px solid ${colors.PRIMARY};
   background: transparent;
   color: ${colors.PRIMARY};
   cursor: pointer;
   white-space: nowrap;
   &:hover { background: ${colors.PRIMARY}; color: ${colors.WHITE}; }
`;
const ResellerStatusNote = styled.div<{ $status: string }>`
   margin-top: 6px;
   font-size: 11px;
   font-weight: 700;
   color: ${({ $status }) =>
      $status === 'approved'
         ? colors.SUCCESS
         : $status === 'pending'
         ? colors.WARNING ?? '#b45309'
         : colors.GRAY5};
`;
const CancelResellerLink = styled.button`
   margin-top: 4px;
   font-family: inherit;
   font-size: 11px;
   font-weight: 600;
   background: none;
   border: none;
   padding: 0;
   color: ${colors.GRAY5};
   text-decoration: underline;
   cursor: pointer;
   &:hover { color: ${colors.DANGER ?? '#dc2626'}; }
`;
const CancelResellerConfirm = styled.div`
   margin-top: 4px;
   display: flex;
   align-items: center;
   gap: 8px;
   font-size: 11px;
   color: ${colors.GRAY5};
`;
const MiniLink = styled.button<{ $danger?: boolean }>`
   font-family: inherit;
   font-size: 11px;
   font-weight: 700;
   background: none;
   border: none;
   padding: 0;
   text-decoration: underline;
   cursor: pointer;
   color: ${({ $danger }) => ($danger ? colors.DANGER ?? '#dc2626' : colors.GRAY5)};
   &:disabled { opacity: 0.5; cursor: default; }
`;
const RModalOverlay = styled.div`
   position: fixed;
   inset: 0;
   background: rgba(0, 0, 0, 0.5);
   display: flex;
   align-items: center;
   justify-content: center;
   z-index: 1000;
   padding: 16px;
`;
const RModalCard = styled.div`
   background: ${colors.WHITE};
   border-radius: 14px;
   padding: 22px;
   width: 100%;
   max-width: 420px;
   box-shadow: 0 20px 50px rgba(0, 0, 0, 0.3);
`;
const RModalTitle = styled.h3`
   margin: 0 0 4px;
   font-size: 18px;
   color: ${colors.MAIN_DARK};
`;
const RModalSub = styled.p`
   margin: 0 0 16px;
   font-size: 13px;
   color: ${colors.GRAY5};
   line-height: 1.5;
`;
const RModalLabel = styled.label`
   display: block;
   font-size: 11px;
   font-weight: 700;
   text-transform: uppercase;
   letter-spacing: 0.04em;
   color: ${colors.GRAY3};
   margin: 12px 0 4px;
`;
const RModalInput = styled.input`
   width: 100%;
   font-family: inherit;
   font-size: 14px;
   padding: 9px 11px;
   border: 1px solid ${colors.GRAY12};
   border-radius: 8px;
   box-sizing: border-box;
   &:focus { outline: none; border-color: ${colors.PRIMARY}; }
`;
const RModalTextarea = styled.textarea`
   width: 100%;
   font-family: inherit;
   font-size: 14px;
   padding: 9px 11px;
   border: 1px solid ${colors.GRAY12};
   border-radius: 8px;
   box-sizing: border-box;
   resize: vertical;
   &:focus { outline: none; border-color: ${colors.PRIMARY}; }
`;
const RModalFooter = styled.div`
   display: flex;
   justify-content: flex-end;
   gap: 10px;
   margin-top: 20px;
`;
const RModalCancel = styled.button`
   font-family: inherit;
   font-size: 13px;
   font-weight: 600;
   padding: 9px 16px;
   border-radius: 8px;
   border: 1px solid ${colors.GRAY12};
   background: ${colors.WHITE};
   color: ${colors.GRAY3};
   cursor: pointer;
   &:disabled { opacity: 0.5; cursor: default; }
`;
const RModalSubmit = styled.button`
   font-family: inherit;
   font-size: 13px;
   font-weight: 700;
   padding: 9px 18px;
   border-radius: 8px;
   border: none;
   background: ${colors.PRIMARY};
   color: ${colors.WHITE};
   cursor: pointer;
   &:disabled { opacity: 0.5; cursor: default; }
`;

/* ─── Distributor: My Resellers (approve / reject) ─────────────────── */

const MyResellersPane: React.FC = () => {
   const qc = useQueryClient();
   const { data, isLoading } = useQuery({
      queryKey: ['vendo', 'my-resellers'],
      queryFn: () => listMyResellersApi(),
   });
   const resellers: ApiReseller[] = data?.resellers ?? [];
   const decide = useMutation({
      mutationFn: (v: { id: string; decision: 'approve' | 'reject' }) =>
         decideResellerApi(v.id, v.decision),
      onSuccess: (_d, v) => {
         toast.success(v.decision === 'approve' ? 'Reseller approved' : 'Reseller rejected');
         qc.invalidateQueries({ queryKey: ['vendo', 'my-resellers'] });
      },
      onError: (e: any) => toast.error(e?.response?.data?.error || 'Action failed'),
   });

   if (isLoading) return <SkeletonLines lines={3} />;
   if (resellers.length === 0)
      return (
         <PointsEmpty>
            No reseller applications yet. When someone applies to resell under
            you, they'll show up here to approve. 🤝
         </PointsEmpty>
      );

   const pending = resellers.filter((r) => r.status === 'pending');
   const others = resellers.filter((r) => r.status !== 'pending');
   return (
      <ResellerWrap>
         {pending.length > 0 && (
            <ResellerSectionTitle>Pending ({pending.length})</ResellerSectionTitle>
         )}
         {pending.map((r) => (
            <ResellerCard key={r._id}>
               <ResellerCardHead>
                  <div>
                     <ResellerName>{r.displayName}</ResellerName>
                     <ResellerMeta>
                        {r.contactPhone || 'no phone'}
                        {r.note ? ` · ${r.note}` : ''}
                     </ResellerMeta>
                     {r.govIdUrl && (
                        <ResellerGovIdLink
                           href={r.govIdUrl}
                           target="_blank"
                           rel="noreferrer"
                        >
                           📎 View government ID
                        </ResellerGovIdLink>
                     )}
                     {r.checklist && Object.values(r.checklist).some(Boolean) && (
                        <ResellerMeta>
                           ✓{' '}
                           {Object.entries(r.checklist)
                              .filter(([, v]) => v)
                              .map(([k]) => k)
                              .join(', ')}
                        </ResellerMeta>
                     )}
                  </div>
                  <ResellerActions>
                     <ResellerReject
                        disabled={decide.isPending}
                        onClick={() => decide.mutate({ id: r._id, decision: 'reject' })}
                     >
                        Reject
                     </ResellerReject>
                     <ResellerApprove
                        disabled={decide.isPending}
                        onClick={() => decide.mutate({ id: r._id, decision: 'approve' })}
                     >
                        Approve
                     </ResellerApprove>
                  </ResellerActions>
               </ResellerCardHead>
            </ResellerCard>
         ))}
         {others.length > 0 && <ResellerSectionTitle>Resellers</ResellerSectionTitle>}
         {others.map((r) => (
            <ResellerCard key={r._id}>
               <ResellerCardHead>
                  <div>
                     <ResellerName>{r.displayName}</ResellerName>
                     <ResellerMeta>{r.contactPhone || 'no phone'}</ResellerMeta>
                  </div>
                  {/* 'upgraded' = they became a distributor themselves, so they
                      are no longer under this upline. Spell it out — a bare
                      "Upgraded" reads like an account tier. */}
                  <ResellerStatusPill $status={r.status}>
                     {r.status === 'upgraded' ? 'Upgraded to distributor' : r.status}
                  </ResellerStatusPill>
               </ResellerCardHead>
               {r.status === 'approved' && r.stats && (
                  <ResellerStatsGrid>
                     <ResellerStat>
                        <ResellerStatVal>
                           {r.stats.points.toLocaleString()}
                        </ResellerStatVal>
                        <ResellerStatLbl>Points</ResellerStatLbl>
                     </ResellerStat>
                     <ResellerStat>
                        <ResellerStatVal>
                           {r.stats.creditsLeft.toLocaleString()}
                        </ResellerStatVal>
                        <ResellerStatLbl>Credits left</ResellerStatLbl>
                     </ResellerStat>
                     <ResellerStat>
                        <ResellerStatVal>
                           {r.stats.creditsSold.toLocaleString()}
                        </ResellerStatVal>
                        <ResellerStatLbl>Credits bought</ResellerStatLbl>
                     </ResellerStat>
                     <ResellerStat>
                        <ResellerStatVal>
                           ₱{r.stats.revenue.toLocaleString()}
                        </ResellerStatVal>
                        <ResellerStatLbl>Revenue</ResellerStatLbl>
                     </ResellerStat>
                     <ResellerStat>
                        <ResellerStatVal>
                           {r.stats.transactions.toLocaleString()}
                        </ResellerStatVal>
                        <ResellerStatLbl>Transactions</ResellerStatLbl>
                     </ResellerStat>
                  </ResellerStatsGrid>
               )}
            </ResellerCard>
         ))}
      </ResellerWrap>
   );
};

const ResellerWrap = styled.div`
   display: flex;
   flex-direction: column;
   gap: 10px;
   width: 100%;
`;
const ResellerCard = styled.div`
   background: ${colors.WHITE};
   border: 1px solid ${colors.GRAY12};
   border-radius: 10px;
   padding: 12px 16px;
   display: flex;
   align-items: center;
   justify-content: space-between;
   gap: 16px;
   flex-wrap: wrap;
`;
const ResellerCardHead = styled.div`
   display: flex;
   align-items: center;
   gap: 10px;
   flex-shrink: 0;
`;
const ResellerGovIdLink = styled.a`
   display: inline-block;
   margin-top: 6px;
   font-size: 12px;
   font-weight: 600;
   color: ${colors.PRIMARY};
   text-decoration: none;
   &:hover {
      text-decoration: underline;
   }
`;
const ResellerStatsGrid = styled.div`
   display: flex;
   align-items: center;
   gap: 18px;
   flex-wrap: wrap;
   justify-content: flex-end;
`;
const ResellerStat = styled.div`
   text-align: center;
`;
const ResellerStatVal = styled.div`
   font-size: 18px;
   font-weight: 800;
   color: ${colors.MAIN_DARK};
`;
const ResellerStatLbl = styled.div`
   font-size: 11px;
   color: ${colors.GRAY5};
   margin-top: 2px;
`;
const ResellerSectionTitle = styled.h3`
   margin: 8px 0 0;
   font-size: 13px;
   font-weight: 700;
   color: ${colors.GRAY3};
   text-transform: uppercase;
   letter-spacing: 0.04em;
`;
const ResellerRow = styled.div`
   display: flex;
   justify-content: space-between;
   align-items: center;
   gap: 12px;
   padding: 12px 16px;
   background: ${colors.WHITE};
   border: 1px solid ${colors.GRAY12};
   border-radius: 10px;
`;
const ResellerName = styled.div`
   font-size: 14px;
   font-weight: 700;
   color: ${colors.MAIN_DARK};
`;
const ResellerMeta = styled.div`
   font-size: 12px;
   color: ${colors.GRAY5};
   margin-top: 2px;
`;
const ResellerActions = styled.div`
   display: flex;
   gap: 8px;
`;
const ResellerApprove = styled.button`
   font-family: inherit;
   font-size: 12px;
   font-weight: 700;
   padding: 7px 14px;
   border-radius: 8px;
   border: none;
   background: ${colors.SUCCESS};
   color: ${colors.WHITE};
   cursor: pointer;
   &:disabled { opacity: 0.5; cursor: default; }
`;
const ResellerReject = styled.button`
   font-family: inherit;
   font-size: 12px;
   font-weight: 700;
   padding: 7px 14px;
   border-radius: 8px;
   border: 1px solid ${colors.GRAY12};
   background: ${colors.WHITE};
   color: ${colors.GRAY3};
   cursor: pointer;
   &:disabled { opacity: 0.5; cursor: default; }
`;
const ResellerStatusPill = styled.span<{ $status: string }>`
   font-size: 11px;
   font-weight: 700;
   text-transform: capitalize;
   padding: 4px 10px;
   border-radius: 999px;
   color: ${({ $status }) =>
      $status === 'approved'
         ? colors.SUCCESS
         : $status === 'upgraded'
         ? colors.PRIMARY
         : colors.GRAY5};
   background: ${colors.GRAY14};
`;

/* ─── Referral card (share link, phase 3) ──────────────────────────── */

const ReferralCard: React.FC = () => {
   const { data } = useQuery({
      queryKey: ['vendo', 'referral'],
      queryFn: getMyReferralApi,
   });
   const code = data?.code;
   const link = code ? `${window.location.origin}/?ref=${code}` : '';
   const copy = () => {
      if (!link) return;
      navigator.clipboard
         .writeText(link)
         .then(() => toast.success('Invite link copied!'))
         .catch(() => toast.error('Could not copy'));
   };
   return (
      <ReferralWrap>
         <ReferralTitle>🎉 Refer &amp; earn</ReferralTitle>
         <ReferralDesc>
            Share your link. When someone you invite buys credits from SmartFi,
            you earn <strong>10 points per credit</strong>.
         </ReferralDesc>
         <ReferralLinkRow>
            <ReferralLinkBox>{link || 'Loading…'}</ReferralLinkBox>
            <ReferralCopyBtn onClick={copy} disabled={!link}>
               Copy
            </ReferralCopyBtn>
         </ReferralLinkRow>
      </ReferralWrap>
   );
};

const ReferralWrap = styled.div`
   background: ${colors.GRAY14};
   border: 1px dashed ${colors.PRIMARY};
   border-radius: 14px;
   padding: 16px 18px;
`;
const ReferralTitle = styled.div`
   font-size: 15px;
   font-weight: 800;
   color: ${colors.MAIN_DARK};
`;
const ReferralDesc = styled.div`
   font-size: 12px;
   color: ${colors.GRAY5};
   margin: 4px 0 12px;
   line-height: 1.5;
`;
const ReferralLinkRow = styled.div`
   display: flex;
   gap: 8px;
   align-items: stretch;
`;
const ReferralLinkBox = styled.div`
   flex: 1;
   font-size: 12px;
   font-family: 'SF Mono', ui-monospace, Menlo, monospace;
   color: ${colors.MAIN_DARK};
   background: ${colors.WHITE};
   border: 1px solid ${colors.GRAY12};
   border-radius: 8px;
   padding: 9px 11px;
   overflow: hidden;
   text-overflow: ellipsis;
   white-space: nowrap;
   display: flex;
   align-items: center;
`;
const ReferralCopyBtn = styled.button`
   font-family: inherit;
   font-size: 12px;
   font-weight: 700;
   padding: 0 16px;
   border-radius: 8px;
   border: none;
   background: ${colors.PRIMARY};
   color: ${colors.WHITE};
   cursor: pointer;
   &:disabled { opacity: 0.5; cursor: default; }
`;

// ── Partners list view + online (Ray 2026-10-27) ──
const ViewToggle = styled.div`
   display: inline-flex;
   border: 1px solid ${colors.GRAY12};
   border-radius: 8px;
   overflow: hidden;
   margin-left: 12px;
`;
const ViewBtn = styled.button<{ $active: boolean }>`
   background: ${({ $active }) => ($active ? colors.PRIMARY : 'transparent')};
   color: ${({ $active }) => ($active ? 'white' : colors.GRAY3)};
   border: none;
   padding: 6px 14px;
   font-size: 12px;
   font-weight: 600;
   cursor: pointer;
`;
const PartnerTable = styled.div`
   display: grid;
   gap: 8px;
   margin-top: 12px;
`;
const PartnerLine = styled.div`
   display: grid;
   grid-template-columns: 44px minmax(0, 1fr) 80px 90px 90px auto;
   align-items: center;
   gap: 14px;
   padding: 10px 14px;
   border: 1px solid ${colors.GRAY12};
   border-radius: 12px;
   background: var(--app-surface, #fff);
   @media (max-width: 900px) {
      grid-template-columns: 44px minmax(0, 1fr) repeat(3, auto);
      > :last-child {
         grid-column: 1 / -1;
      }
   }
   @media (max-width: 560px) {
      grid-template-columns: 44px minmax(0, 1fr);
      > :nth-child(3),
      > :nth-child(4),
      > :nth-child(5) {
         display: none;
      }
   }
`;
const LineAvatar = styled.div`
   position: relative;
   width: 44px;
   height: 44px;
   border-radius: 50%;
   background: ${colors.GRAY14};
   display: grid;
   place-items: center;
   font-weight: 800;
   color: ${colors.GRAY3};
   /* Pinned to the circle: inside the grid, height: 100% let a tall photo stretch the row (Aztech, Kim Lloyd). */
   img {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      border-radius: 50%;
      object-fit: cover;
   }
`;
const OnlineDot = styled.span`
   position: absolute;
   right: 0;
   bottom: 0;
   width: 12px;
   height: 12px;
   border-radius: 50%;
   background: #22c55e;
   border: 2px solid var(--app-surface, #fff);
`;
const OnlineTag = styled.span`
   margin-left: 8px;
   padding: 1px 7px;
   border-radius: 999px;
   font-size: 10.5px;
   font-weight: 800;
   letter-spacing: 0.04em;
   color: #16934a;
   background: rgba(34, 197, 94, 0.12);
   vertical-align: middle;
`;
const LineMain = styled.div`
   min-width: 0;
`;
const LineName = styled.div`
   font-weight: 800;
   font-size: 14.5px;
   overflow: hidden;
   text-overflow: ellipsis;
   white-space: nowrap;
`;
const LineSub = styled.div`
   font-size: 12px;
   color: ${colors.GRAY3};
   overflow: hidden;
   text-overflow: ellipsis;
   white-space: nowrap;
`;
const LineStat = styled.div`
   display: grid;
   small {
      font-size: 10.5px;
      font-weight: 700;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      color: ${colors.GRAY3};
   }
   b {
      font-size: 14px;
   }
`;
const LineActions = styled.div`
   display: flex;
   align-items: center;
   gap: 8px;
   flex-wrap: wrap;
   justify-content: flex-end;
`;
const LineBtn = styled.button<{ $primary?: boolean }>`
   padding: 7px 14px;
   border-radius: 8px;
   font-size: 12.5px;
   font-weight: 700;
   cursor: pointer;
   border: 1px solid ${({ $primary }) => ($primary ? '#e8a33a' : colors.GRAY12)};
   background: ${({ $primary }) => ($primary ? 'linear-gradient(90deg, #e8a33a, #f2c14e)' : 'transparent')};
   color: ${({ $primary }) => ($primary ? '#fff' : 'inherit')};
   white-space: nowrap;
`;
const LineLink = styled.button`
   border: 0;
   background: none;
   padding: 0;
   font-size: 12px;
   font-weight: 600;
   color: ${colors.PRIMARY};
   cursor: pointer;
   text-decoration: underline;
`;
